/** The pixel font, authored as data.
 *
 * Every reading the game draws *into* the world - a stack's count plaque, a
 * damage floater, a resource total, a HUD figure, the caption over it - is a
 * bitmap glyph from this table. The classic's type is hard-edged two-colour marks
 * with a dark keyline, and that is not a stylistic flourish: it is why a `42`
 * stays readable at 10px over a bright angel and over near-black wood alike. An
 * anti-aliased vector numeral over hard-quantised pixel art is the same tell as
 * a bilinear-scaled sprite - measured on the shipped battle screenshot, one
 * count plaque interior carried 157 unique colours across 319px, 0.49
 * unique-per-pixel, against the 0.02 a two-colour numeral produces.
 *
 * The table is DATA and it is authored here rather than fetched: a font file
 * would be a build input nobody in the repo could edit, and a system face would
 * render differently on every machine, which breaks the screenshot harness.
 *
 * ## Editing a glyph
 *
 * Each entry is `g(width, ...rows)`. Rows run top to bottom and each row is a
 * binary literal whose **most significant bit is the leftmost column**. Write
 * the row literals with exactly `width` binary digits and the ASCII picture
 * above the entry stays honest; `render/test/bitmap-text.spec.ts` asserts that
 * every row fits its declared width, that the pictures match the masks, and
 * that no glyph is blank.
 *
 * ## There are EIGHT faces in this file, in two families
 *
 * The table immediately below is the HUD face - cap 7, x-height 5 - and it is
 * the one every tuned call site in the build was measured against. Seven more
 * are authored at the bottom of the file, as ASCII pictures rather than binary
 * literals, because the classic ships three and one seven-row face cannot do the work
 * of three: `text` at cap 9 / x 6 (its `smallfont`), `display` at cap 11 with
 * two-pixel stems (its `bigfont`), `tall` - cap 11 at the text face's
 * widths - which exists so a reading held to the device-pixel floor
 * (`devicePixels.ts`) can be promoted without growing half again as wide, and
 * `caption` - cap 15 / x 11, two-pixel stems - the face that floor promotes
 * to on a 1080-tall window, where the floor is 15 (see `deviceCapFloor`).
 *
 * Those five are one SANS family. The other three - `roman` (cap 11),
 * `romanDisplay` (14) and `romanCaption` (15) - are a condensed roman serif
 * at the same three caps the floor lands on, and they are what the HUD, the
 * adventure sidebar, the resource bars and the arena are set in, because
 * every panel of the commercial game is a serif and a blind reader named our
 * sans as the tell eight times out of eight. See `ROMAN_PICTURES`. A family
 * is never crossed by a floor promotion (`FAMILIES` in the React half): a
 * caption that changed letterform between two zoom levels would be a
 * different caption. See `FACES` and the note above it.
 *
 * A call site names a face; nothing here folds one into another, and every
 * face carries the same key set so a string never changes its wording when a
 * screen changes its size.
 *
 * ## The metrics, and why a glyph may be nine rows tall
 *
 * `GLYPH_H` rows are the **body**: cap height, digit height, and the baseline
 * on the last of them. Everything the HUD counted with before this face grew a
 * lowercase fits in exactly those seven rows, which is why they have not moved.
 *
 * A glyph may declare up to `DESCENT` further rows that hang BELOW the
 * baseline. They are ink, not metric: `LINE_H` - the box a label reports to the
 * call site that positions it - is the body plus its keyline and nothing else,
 * so a string that gains a comma does not become a taller line and does not
 * shift a plaque that was centred on it. Descenders overflow the line box the
 * way they do in any real face.
 *
 * That band exists because a comma cannot be drawn without it. Authored inside
 * the body the mark is `##` over `#.` sitting on the baseline, and against the
 * single pixel of a full stop that is a two-pixel difference: on a wood plate
 * at magnification 2 the adventure bar's `20,000` read as `20.000` and the
 * town's income read as `4.000/DAY`. The classic's own smallfont hangs its comma two
 * rows under the baseline - measured off the expansion hero screen's
 * `Month: 1, Week: 4, Day: 5`, the comma occupies rows -1..+2 around a baseline
 * its capitals stop on - and so does this one.
 *
 * Digits are 4 columns wide and capitals 5, deliberately. All ten digits share
 * one width so a changing count does not jitter (tabular figures), and four
 * columns is what lets a four-digit stack count fit the 23px count plaque
 * `grid.ts` defines, which a 5-wide digit does not.
 */

// The DOM renderer reaches this file through the package's `./text/glyphs`
// subpath - the only one exported besides the barrel, and the barrel pulls
// Pixi in. The device-pixel rules it needs travel through the same door; they
// are pure and window-free, and live in `devicePixels.ts` beside the layout.
export {
  MIN_DEVICE_CAP, deviceCapFloor, effectiveDevicePixelRatio, holdToFloor, measureDeviceScale,
  snapDeviceMagnification, windowCapFloor,
} from "./devicePixels.js";
export type { DeviceMagnification } from "./devicePixels.js";

/** Body rows per glyph: cap height and digit height, baseline on the last. */
export const GLYPH_H = 7;

/** Rows a glyph may hang below the baseline. Not part of the line box. */
export const DESCENT = 2;

/** The most rows any one design may declare. */
export const GLYPH_ROWS = GLYPH_H + DESCENT;

/** Blank columns left between two glyphs. */
export const TRACKING = 1;

/** The keyline dilated around every glyph, on all four sides, in glyph pixels. */
export const OUTLINE = 1;

export interface Glyph {
  /** Columns of ink. The advance is `w + TRACKING`. */
  readonly w: number;
  /**
   * Rows of this glyph that are BODY - cap height for its face, baseline on the
   * last of them. Carried per glyph rather than read off a module constant
   * because there are three faces now and a `y` has to know which baseline it
   * hangs from without being told.
   */
  readonly body: number;
  /**
   * Row bitmasks, top row first, bit `w-1` leftmost. Exactly `body` of them for
   * a glyph that sits on the baseline; more for one that hangs below it, in
   * which case the last row carries ink.
   */
  readonly rows: readonly number[];
}

function g(w: number, ...rows: readonly number[]): Glyph {
  return { w, body: GLYPH_H, rows };
}

/** How far below the baseline this design hangs, in glyph pixels. */
export function descentOf(glyph: Glyph): number {
  return Math.max(0, glyph.rows.length - glyph.body);
}

/** The deepest descender in `text`, in glyph pixels. 0 for digits alone. */
export function descentOfText(text: string, face?: FaceId): number {
  let d = 0;
  for (let i = 0; i < text.length; i++) {
    const n = descentOf(glyphFor(text.charAt(i), face));
    if (n > d) d = n;
  }
  return d;
}

/**
 * The face: digits, capitals, lowercase, and the punctuation a reading or a
 * caption actually uses - `1,240`, `62/90`, `+2`, `-1`, `4.5`, `75%`, `12-18`,
 * `Spell Points (12)`, `Month 1, Week 4`.
 *
 * Lowercase is present so that a label does not have to leave the face to be
 * set. The classic sets EVERY string in a bitmap face - `Experience`, `Spell Points`,
 * `View Band of Forgetting Info` are all stamped, not typeset - and a screen that
 * mixes an anti-aliased serif caption with a bitmap figure beside it reads as
 * two programs. `foldGlyphKey` therefore no longer folds `a` up to `A`: it did
 * only because there was no `a` to fold to, and folding turned every caption
 * into small caps whether the call site wanted them or not. A character with no
 * design at all still draws `?`, so a missing glyph is visible in a screenshot
 * rather than silently swallowing a reading.
 */
export const GLYPHS: Readonly<Record<string, Glyph>> = Object.freeze({
  // ...
  // ...
  // ...
  // ...
  // ...
  // ...
  // ...
  " ": g(3, 0b000, 0b000, 0b000, 0b000, 0b000, 0b000, 0b000),
  // .##.
  // #..#
  // #..#
  // #..#
  // #..#
  // #..#
  // .##.
  "0": g(4, 0b0110, 0b1001, 0b1001, 0b1001, 0b1001, 0b1001, 0b0110),
  // ..#.
  // .##.
  // ..#.
  // ..#.
  // ..#.
  // ..#.
  // .###
  "1": g(4, 0b0010, 0b0110, 0b0010, 0b0010, 0b0010, 0b0010, 0b0111),
  // .##.
  // #..#
  // ...#
  // ..#.
  // .#..
  // #...
  // ####
  "2": g(4, 0b0110, 0b1001, 0b0001, 0b0010, 0b0100, 0b1000, 0b1111),
  // ####
  // ...#
  // ..#.
  // .##.
  // ...#
  // #..#
  // .##.
  "3": g(4, 0b1111, 0b0001, 0b0010, 0b0110, 0b0001, 0b1001, 0b0110),
  // The apex is OPEN. A closed square bowl - `#..#` over `#..#` over `####` -
  // is the shape of Cyrillic TSE, not of a four, and this face draws the two
  // most-read numerals in the game: the battle count plates and the mana
  // readout. The diagonal is what tells a reader which one it is at 7 rows.
  // ...#
  // ..##
  // .#.#
  // #..#
  // ####
  // ...#
  // ...#
  "4": g(4, 0b0001, 0b0011, 0b0101, 0b1001, 0b1111, 0b0001, 0b0001),
  // ####
  // #...
  // ###.
  // ...#
  // ...#
  // #..#
  // .##.
  "5": g(4, 0b1111, 0b1000, 0b1110, 0b0001, 0b0001, 0b1001, 0b0110),
  // The flag leans. A `6` whose left stem runs the full cap height with a
  // closed head on top is a `b`; the six is the stem falling away from the
  // top RIGHT, which is the one stroke that separates the two at this size.
  // ..##
  // .#..
  // #...
  // ###.
  // #..#
  // #..#
  // .##.
  "6": g(4, 0b0011, 0b0100, 0b1000, 0b1110, 0b1001, 0b1001, 0b0110),
  // ####
  // ...#
  // ...#
  // ..#.
  // ..#.
  // .#..
  // .#..
  "7": g(4, 0b1111, 0b0001, 0b0001, 0b0010, 0b0010, 0b0100, 0b0100),
  // .##.
  // #..#
  // #..#
  // .##.
  // #..#
  // #..#
  // .##.
  "8": g(4, 0b0110, 0b1001, 0b1001, 0b0110, 0b1001, 0b1001, 0b0110),
  // .##.
  // #..#
  // #..#
  // .###
  // ...#
  // #..#
  // .##.
  "9": g(4, 0b0110, 0b1001, 0b1001, 0b0111, 0b0001, 0b1001, 0b0110),
  // .###.
  // #...#
  // #...#
  // #####
  // #...#
  // #...#
  // #...#
  "A": g(5, 0b01110, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001),
  // ####.
  // #...#
  // #...#
  // ####.
  // #...#
  // #...#
  // ####.
  "B": g(5, 0b11110, 0b10001, 0b10001, 0b11110, 0b10001, 0b10001, 0b11110),
  // .###.
  // #...#
  // #....
  // #....
  // #....
  // #...#
  // .###.
  "C": g(5, 0b01110, 0b10001, 0b10000, 0b10000, 0b10000, 0b10001, 0b01110),
  // ####.
  // #...#
  // #...#
  // #...#
  // #...#
  // #...#
  // ####.
  "D": g(5, 0b11110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b11110),
  // #####
  // #....
  // #....
  // ####.
  // #....
  // #....
  // #####
  "E": g(5, 0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b11111),
  // #####
  // #....
  // #....
  // ####.
  // #....
  // #....
  // #....
  "F": g(5, 0b11111, 0b10000, 0b10000, 0b11110, 0b10000, 0b10000, 0b10000),
  // .###.
  // #...#
  // #....
  // #.###
  // #...#
  // #...#
  // .###.
  "G": g(5, 0b01110, 0b10001, 0b10000, 0b10111, 0b10001, 0b10001, 0b01110),
  // #...#
  // #...#
  // #...#
  // #####
  // #...#
  // #...#
  // #...#
  "H": g(5, 0b10001, 0b10001, 0b10001, 0b11111, 0b10001, 0b10001, 0b10001),
  // ###
  // .#.
  // .#.
  // .#.
  // .#.
  // .#.
  // ###
  "I": g(3, 0b111, 0b010, 0b010, 0b010, 0b010, 0b010, 0b111),
  // ..###
  // ...#.
  // ...#.
  // ...#.
  // ...#.
  // #..#.
  // .##..
  "J": g(5, 0b00111, 0b00010, 0b00010, 0b00010, 0b00010, 0b10010, 0b01100),
  // #...#
  // #..#.
  // #.#..
  // ##...
  // #.#..
  // #..#.
  // #...#
  "K": g(5, 0b10001, 0b10010, 0b10100, 0b11000, 0b10100, 0b10010, 0b10001),
  // #....
  // #....
  // #....
  // #....
  // #....
  // #....
  // #####
  "L": g(5, 0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b10000, 0b11111),
  // #...#
  // ##.##
  // #.#.#
  // #...#
  // #...#
  // #...#
  // #...#
  "M": g(5, 0b10001, 0b11011, 0b10101, 0b10001, 0b10001, 0b10001, 0b10001),
  // #...#
  // ##..#
  // #.#.#
  // #..##
  // #...#
  // #...#
  // #...#
  "N": g(5, 0b10001, 0b11001, 0b10101, 0b10011, 0b10001, 0b10001, 0b10001),
  // .###.
  // #...#
  // #...#
  // #...#
  // #...#
  // #...#
  // .###.
  "O": g(5, 0b01110, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110),
  // ####.
  // #...#
  // #...#
  // ####.
  // #....
  // #....
  // #....
  "P": g(5, 0b11110, 0b10001, 0b10001, 0b11110, 0b10000, 0b10000, 0b10000),
  // .###.
  // #...#
  // #...#
  // #...#
  // #.#.#
  // #..#.
  // .##.#
  "Q": g(5, 0b01110, 0b10001, 0b10001, 0b10001, 0b10101, 0b10010, 0b01101),
  // ####.
  // #...#
  // #...#
  // ####.
  // #.#..
  // #..#.
  // #...#
  "R": g(5, 0b11110, 0b10001, 0b10001, 0b11110, 0b10100, 0b10010, 0b10001),
  // .####
  // #....
  // #....
  // .###.
  // ....#
  // ....#
  // ####.
  "S": g(5, 0b01111, 0b10000, 0b10000, 0b01110, 0b00001, 0b00001, 0b11110),
  // #####
  // ..#..
  // ..#..
  // ..#..
  // ..#..
  // ..#..
  // ..#..
  "T": g(5, 0b11111, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100, 0b00100),
  // #...#
  // #...#
  // #...#
  // #...#
  // #...#
  // #...#
  // .###.
  "U": g(5, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01110),
  // #...#
  // #...#
  // #...#
  // #...#
  // #...#
  // .#.#.
  // ..#..
  "V": g(5, 0b10001, 0b10001, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100),
  // #...#
  // #...#
  // #...#
  // #.#.#
  // #.#.#
  // ##.##
  // #...#
  "W": g(5, 0b10001, 0b10001, 0b10001, 0b10101, 0b10101, 0b11011, 0b10001),
  // #...#
  // #...#
  // .#.#.
  // ..#..
  // .#.#.
  // #...#
  // #...#
  "X": g(5, 0b10001, 0b10001, 0b01010, 0b00100, 0b01010, 0b10001, 0b10001),
  // #...#
  // #...#
  // .#.#.
  // ..#..
  // ..#..
  // ..#..
  // ..#..
  "Y": g(5, 0b10001, 0b10001, 0b01010, 0b00100, 0b00100, 0b00100, 0b00100),
  // #####
  // ....#
  // ...#.
  // ..#..
  // .#...
  // #....
  // #####
  "Z": g(5, 0b11111, 0b00001, 0b00010, 0b00100, 0b01000, 0b10000, 0b11111),
  // ....
  // ....
  // .##.
  // ...#
  // .###
  // #..#
  // .###
  "a": g(4, 0b0000, 0b0000, 0b0110, 0b0001, 0b0111, 0b1001, 0b0111),
  // #...
  // #...
  // ###.
  // #..#
  // #..#
  // #..#
  // ###.
  "b": g(4, 0b1000, 0b1000, 0b1110, 0b1001, 0b1001, 0b1001, 0b1110),
  // ....
  // ....
  // .##.
  // #..#
  // #...
  // #..#
  // .##.
  "c": g(4, 0b0000, 0b0000, 0b0110, 0b1001, 0b1000, 0b1001, 0b0110),
  // ...#
  // ...#
  // .###
  // #..#
  // #..#
  // #..#
  // .###
  "d": g(4, 0b0001, 0b0001, 0b0111, 0b1001, 0b1001, 0b1001, 0b0111),
  // ....
  // ....
  // .##.
  // #..#
  // ####
  // #...
  // .##.
  "e": g(4, 0b0000, 0b0000, 0b0110, 0b1001, 0b1111, 0b1000, 0b0110),
  // .##
  // #..
  // ###
  // #..
  // #..
  // #..
  // #..
  "f": g(3, 0b011, 0b100, 0b111, 0b100, 0b100, 0b100, 0b100),
  // ....
  // ....
  // .###
  // #..#
  // #..#
  // .###
  // ...#
  // #..#
  // .##.
  "g": g(4, 0b0000, 0b0000, 0b0111, 0b1001, 0b1001, 0b0111, 0b0001, 0b1001, 0b0110),
  // #...
  // #...
  // ###.
  // #..#
  // #..#
  // #..#
  // #..#
  "h": g(4, 0b1000, 0b1000, 0b1110, 0b1001, 0b1001, 0b1001, 0b1001),
  // #
  // .
  // #
  // #
  // #
  // #
  // #
  "i": g(1, 0b1, 0b0, 0b1, 0b1, 0b1, 0b1, 0b1),
  // ..#
  // ...
  // ..#
  // ..#
  // ..#
  // ..#
  // ..#
  // ..#
  // ##.
  "j": g(3, 0b001, 0b000, 0b001, 0b001, 0b001, 0b001, 0b001, 0b001, 0b110),
  // #...
  // #...
  // #..#
  // #.#.
  // ##..
  // #.#.
  // #..#
  "k": g(4, 0b1000, 0b1000, 0b1001, 0b1010, 0b1100, 0b1010, 0b1001),
  // #
  // #
  // #
  // #
  // #
  // #
  // #
  "l": g(1, 0b1, 0b1, 0b1, 0b1, 0b1, 0b1, 0b1),
  // .....
  // .....
  // ##.##
  // #.#.#
  // #.#.#
  // #.#.#
  // #.#.#
  "m": g(5, 0b00000, 0b00000, 0b11011, 0b10101, 0b10101, 0b10101, 0b10101),
  // ....
  // ....
  // ###.
  // #..#
  // #..#
  // #..#
  // #..#
  "n": g(4, 0b0000, 0b0000, 0b1110, 0b1001, 0b1001, 0b1001, 0b1001),
  // ....
  // ....
  // .##.
  // #..#
  // #..#
  // #..#
  // .##.
  "o": g(4, 0b0000, 0b0000, 0b0110, 0b1001, 0b1001, 0b1001, 0b0110),
  // ....
  // ....
  // ###.
  // #..#
  // #..#
  // #..#
  // ###.
  // #...
  // #...
  "p": g(4, 0b0000, 0b0000, 0b1110, 0b1001, 0b1001, 0b1001, 0b1110, 0b1000, 0b1000),
  // ....
  // ....
  // .###
  // #..#
  // #..#
  // #..#
  // .###
  // ...#
  // ...#
  "q": g(4, 0b0000, 0b0000, 0b0111, 0b1001, 0b1001, 0b1001, 0b0111, 0b0001, 0b0001),
  // ...
  // ...
  // ###
  // #..
  // #..
  // #..
  // #..
  "r": g(3, 0b000, 0b000, 0b111, 0b100, 0b100, 0b100, 0b100),
  // ....
  // ....
  // .###
  // #...
  // .##.
  // ...#
  // ###.
  "s": g(4, 0b0000, 0b0000, 0b0111, 0b1000, 0b0110, 0b0001, 0b1110),
  // #..
  // #..
  // ###
  // #..
  // #..
  // #..
  // .##
  "t": g(3, 0b100, 0b100, 0b111, 0b100, 0b100, 0b100, 0b011),
  // ....
  // ....
  // #..#
  // #..#
  // #..#
  // #..#
  // .###
  "u": g(4, 0b0000, 0b0000, 0b1001, 0b1001, 0b1001, 0b1001, 0b0111),
  // .....
  // .....
  // #...#
  // #...#
  // #...#
  // .#.#.
  // ..#..
  "v": g(5, 0b00000, 0b00000, 0b10001, 0b10001, 0b10001, 0b01010, 0b00100),
  // .....
  // .....
  // #...#
  // #...#
  // #.#.#
  // #.#.#
  // .#.#.
  "w": g(5, 0b00000, 0b00000, 0b10001, 0b10001, 0b10101, 0b10101, 0b01010),
  // ....
  // ....
  // #..#
  // #..#
  // .##.
  // #..#
  // #..#
  "x": g(4, 0b0000, 0b0000, 0b1001, 0b1001, 0b0110, 0b1001, 0b1001),
  // ....
  // ....
  // #..#
  // #..#
  // #..#
  // #..#
  // .###
  // ...#
  // ###.
  "y": g(4, 0b0000, 0b0000, 0b1001, 0b1001, 0b1001, 0b1001, 0b0111, 0b0001, 0b1110),
  // ....
  // ....
  // ####
  // ...#
  // .##.
  // #...
  // ####
  "z": g(4, 0b0000, 0b0000, 0b1111, 0b0001, 0b0110, 0b1000, 0b1111),
  // .
  // .
  // .
  // .
  // .
  // .
  // #
  ".": g(1, 0b0, 0b0, 0b0, 0b0, 0b0, 0b0, 0b1),
  // ..
  // ..
  // ..
  // ..
  // ..
  // .#
  // ##
  // #.
  // #.
  ",": g(2, 0b00, 0b00, 0b00, 0b00, 0b00, 0b01, 0b11, 0b10, 0b10),
  // .
  // .
  // #
  // .
  // .
  // .
  // #
  ":": g(1, 0b0, 0b0, 0b1, 0b0, 0b0, 0b0, 0b1),
  // ..
  // ..
  // .#
  // ..
  // ..
  // .#
  // ##
  // #.
  // #.
  ";": g(2, 0b00, 0b00, 0b01, 0b00, 0b00, 0b01, 0b11, 0b10, 0b10),
  // ..#
  // ..#
  // .#.
  // .#.
  // .#.
  // #..
  // #..
  "/": g(3, 0b001, 0b001, 0b010, 0b010, 0b010, 0b100, 0b100),
  // ...
  // ...
  // .#.
  // ###
  // .#.
  // ...
  // ...
  "+": g(3, 0b000, 0b000, 0b010, 0b111, 0b010, 0b000, 0b000),
  // ...
  // ...
  // ...
  // ###
  // ...
  // ...
  // ...
  "-": g(3, 0b000, 0b000, 0b000, 0b111, 0b000, 0b000, 0b000),
  // ...
  // ...
  // ###
  // ...
  // ###
  // ...
  // ...
  "=": g(3, 0b000, 0b000, 0b111, 0b000, 0b111, 0b000, 0b000),
  // ##..#
  // ##.#.
  // ...#.
  // ..#..
  // .#...
  // .#.##
  // #..##
  "%": g(5, 0b11001, 0b11010, 0b00010, 0b00100, 0b01000, 0b01011, 0b10011),
  // #
  // #
  // .
  // .
  // .
  // .
  // .
  "'": g(1, 0b1, 0b1, 0b0, 0b0, 0b0, 0b0, 0b0),
  // #.#
  // #.#
  // ...
  // ...
  // ...
  // ...
  // ...
  "\"": g(3, 0b101, 0b101, 0b000, 0b000, 0b000, 0b000, 0b000),
  // #
  // #
  // #
  // #
  // #
  // .
  // #
  "!": g(1, 0b1, 0b1, 0b1, 0b1, 0b1, 0b0, 0b1),
  // .#
  // #.
  // #.
  // #.
  // #.
  // #.
  // #.
  // .#
  "(": g(2, 0b01, 0b10, 0b10, 0b10, 0b10, 0b10, 0b10, 0b01),
  // #.
  // .#
  // .#
  // .#
  // .#
  // .#
  // .#
  // #.
  ")": g(2, 0b10, 0b01, 0b01, 0b01, 0b01, 0b01, 0b01, 0b10),
  // ...
  // #.#
  // .#.
  // #.#
  // ...
  // ...
  // ...
  "*": g(3, 0b000, 0b101, 0b010, 0b101, 0b000, 0b000, 0b000),
  // ...
  // ...
  // ..#
  // .#.
  // #..
  // .#.
  // ..#
  "<": g(3, 0b000, 0b000, 0b001, 0b010, 0b100, 0b010, 0b001),
  // ...
  // ...
  // #..
  // .#.
  // ..#
  // .#.
  // #..
  ">": g(3, 0b000, 0b000, 0b100, 0b010, 0b001, 0b010, 0b100),
  // .
  // .
  // .
  // #
  // .
  // .
  // .
  "\u00b7": g(1, 0b0, 0b0, 0b0, 0b1, 0b0, 0b0, 0b0),
  // .....
  // .....
  // .....
  // #####
  // .....
  // .....
  // .....
  "\u2014": g(5, 0b00000, 0b00000, 0b00000, 0b11111, 0b00000, 0b00000, 0b00000),
  // .##.
  // #..#
  // ...#
  // ..#.
  // ..#.
  // ....
  // ..#.
  "?": g(4, 0b0110, 0b1001, 0b0001, 0b0010, 0b0010, 0b0000, 0b0010),
});

// ---------------------------------------------------------------------------
// Four faces: three because the classic ships three, and one for the floor
// ---------------------------------------------------------------------------

/**
 * The table above is ONE face. The classic ships three, and the gap is measurable.
 *
 * Its `smallfont` is cap 9 / x-height 6 / descent 2; its `bigfont` is cap 11
 * with two-pixel stems. Ours was cap 7 / x 5 / descent 2 and nothing else, so
 * at 1920x1080 the HUD drew at magnification 2 - fourteen device pixels of cap
 * where the classic's nine-pixel cap on a 600-tall screen is 16.2 at the same screen
 * fraction. Fourteen percent smaller than the original's SMALLEST face, with no
 * text weight and no display weight at all, which is what an anti-aliased serif
 * was called in to cover.
 *
 * The fix is to ADD faces, not to grow the one that exists. Every HUD spot in
 * this build is tuned against the seven-row body - the 23px count plaque, the
 * army socket, the gauge grooves - and moving `GLYPH_H` would relayout the
 * whole build at once. A call site opts into a bigger face when it wants one.
 *
 *   hud     cap 7  / x 5 / desc 2 / 1px stem  - counts, plaques, tight readings
 *   text    cap 9  / x 6 / desc 2 / 1px stem  - panel captions and body labels
 *   tall    cap 11 / x 8 / desc 3 / 2px stem  - the text face's widths at the
 *                                               floor; what a HUD or text
 *                                               reading at magnification 1 is
 *                                               promoted to (see below)
 *   display cap 11 / x 7 / desc 3 / 2px stem  - headings and the biggest figure
 *
 * The fourth face is the one `MIN_DEVICE_CAP` (`devicePixels.ts`) needs. A
 * seven-row HUD reading at one device pixel per texel is 7 rows on the glass,
 * under the floor, and the only move a fixed layout allows is a taller face
 * at the same magnification. The display face clears the floor but is 65%
 * wider than the HUD face on `Movement 1,600 / 1,600` (170 glyph px against
 * ~105), and at 1366x768 the adventure sidebar's gauge groove has room for
 * neither that nor two magnifications: the reading overprinted its caption.
 * `tall` sets the same string at 134 - the text face's 125 plus a bolder
 * stem - and clears the floor by exactly the row the original's smallfont
 * does on a 768-tall window.
 */
export type FaceId =
  | "hud" | "text" | "tall" | "display" | "caption"
  | "roman" | "romanDisplay" | "romanCaption";

export interface Face {
  readonly id: FaceId;
  /** Body rows: cap height and digit height, baseline on the last of them. */
  readonly capH: number;
  /** Rows a lowercase `x` stands in. Documentation and tests, not layout. */
  readonly xHeight: number;
  /** The most rows any design in this face hangs below the baseline. */
  readonly descent: number;
  /** Stroke weight in glyph pixels. 2 for the display face, 1 for the others. */
  readonly stem: number;
  /** Blank columns left between two glyphs of this face. */
  readonly tracking: number;
  readonly glyphs: Readonly<Record<string, Glyph>>;
}

/** The face a call site gets when it does not name one. */
export const DEFAULT_FACE: FaceId = "hud";

/** One picture row per line, `#` lit and `.` clear; every row the same width. */
type Picture = readonly string[];

/**
 * A picture as a glyph.
 *
 * Trailing all-clear rows past the body are dropped, so a design may be written
 * out to full height without claiming a descent it does not use - a glyph that
 * reserved room under the line for a blank row would push every panel rule that
 * owes it clearance down for nothing.
 */
function px(body: number, rows: Picture): Glyph {
  const w = rows[0]?.length ?? 1;
  const masks: number[] = [];
  for (const row of rows) {
    if (row.length !== w) throw new Error(`picture row ${JSON.stringify(row)} is not ${String(w)} wide`);
    let bits = 0;
    for (let c = 0; c < w; c++) bits = (bits << 1) | (row.charAt(c) === "#" ? 1 : 0);
    masks.push(bits);
  }
  while (masks.length < body) masks.push(0);
  while (masks.length > body && masks[masks.length - 1] === 0) masks.pop();
  return { w, body, rows: masks };
}

function pictureFace(
  id: FaceId, capH: number, xHeight: number, descent: number, stem: number,
  tracking: number, table: Readonly<Record<string, Picture>>,
): Face {
  const glyphs: Record<string, Glyph> = {};
  for (const [ch, picture] of Object.entries(table)) glyphs[ch] = px(capH, picture);
  return {
    id, capH, xHeight, descent, stem, tracking, glyphs: Object.freeze(glyphs),
  };
}

/**
 * The text face: cap 9, x-height 6, one-pixel stems, comma and `y` two rows
 * under the baseline. This is the classic's `smallfont` metric, and it is the face a
 * panel caption or a running label should be set in - the seven-row face is a
 * HUD face and reads as one.
 *
 * Digits are five columns and all ten share that width, so a changing reading
 * does not jitter. The picture IS the data here: there is no binary literal to
 * drift from, which is the one thing the seven-row table above needs a test for.
 */
const TEXT_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["....", "....", "....", "....", "....", "....", "....", "....", "...."],
  "0": [".###.", "#...#", "#...#", "#...#", "#...#", "#...#", "#...#", "#...#", ".###."],
  "1": ["..#..", ".##..", "..#..", "..#..", "..#..", "..#..", "..#..", "..#..", "#####"],
  "2": [".###.", "#...#", "#...#", "....#", "...#.", "..#..", ".#...", "#....", "#####"],
  "3": [".###.", "#...#", "....#", "....#", "..##.", "....#", "....#", "#...#", ".###."],
  "4": ["...#.", "..##.", ".#.#.", "#..#.", "#..#.", "#####", "...#.", "...#.", "...#."],
  "5": ["#####", "#....", "#....", "####.", "....#", "....#", "....#", "#...#", ".###."],
  "6": ["..##.", ".#...", "#....", "#....", "####.", "#...#", "#...#", "#...#", ".###."],
  "7": ["#####", "....#", "....#", "...#.", "...#.", "..#..", "..#..", ".#...", ".#..."],
  "8": [".###.", "#...#", "#...#", "#...#", ".###.", "#...#", "#...#", "#...#", ".###."],
  "9": [".###.", "#...#", "#...#", "#...#", ".####", "....#", "....#", "...#.", ".##.."],
  "A": ["..##..", ".#..#.", ".#..#.", "#....#", "#....#", "######", "#....#", "#....#", "#....#"],
  "B": ["#####.", "#....#", "#....#", "#....#", "#####.", "#....#", "#....#", "#....#", "#####."],
  "C": [".####.", "#....#", "#....#", "#.....", "#.....", "#.....", "#....#", "#....#", ".####."],
  "D": ["#####.", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", "#####."],
  "E": ["######", "#.....", "#.....", "#.....", "#####.", "#.....", "#.....", "#.....", "######"],
  "F": ["######", "#.....", "#.....", "#.....", "#####.", "#.....", "#.....", "#.....", "#....."],
  "G": [".####.", "#....#", "#.....", "#.....", "#..###", "#....#", "#....#", "#....#", ".####."],
  "H": ["#....#", "#....#", "#....#", "#....#", "######", "#....#", "#....#", "#....#", "#....#"],
  "I": ["###", ".#.", ".#.", ".#.", ".#.", ".#.", ".#.", ".#.", "###"],
  "J": ["..###", "....#", "....#", "....#", "....#", "....#", "#...#", "#...#", ".###."],
  "K": ["#....#", "#...#.", "#..#..", "#.#...", "##....", "#.#...", "#..#..", "#...#.", "#....#"],
  "L": ["#.....", "#.....", "#.....", "#.....", "#.....", "#.....", "#.....", "#.....", "######"],
  "M": ["#.....#", "##...##", "#.#.#.#", "#..#..#", "#.....#", "#.....#", "#.....#", "#.....#", "#.....#"],
  "N": ["#....#", "##...#", "##...#", "#.#..#", "#..#.#", "#...##", "#...##", "#....#", "#....#"],
  "O": [".####.", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", ".####."],
  "P": ["#####.", "#....#", "#....#", "#....#", "#####.", "#.....", "#.....", "#.....", "#....."],
  "Q": [".####.", "#....#", "#....#", "#....#", "#....#", "#....#", "#..#.#", "#...#.", ".###.#"],
  "R": ["#####.", "#....#", "#....#", "#....#", "#####.", "#..#..", "#...#.", "#....#", "#....#"],
  "S": [".####.", "#....#", "#.....", "#.....", ".####.", ".....#", ".....#", "#....#", ".####."],
  "T": ["#####", "..#..", "..#..", "..#..", "..#..", "..#..", "..#..", "..#..", "..#.."],
  "U": ["#....#", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", "#....#", ".####."],
  "V": ["#...#", "#...#", "#...#", "#...#", "#...#", "#...#", ".#.#.", ".#.#.", "..#.."],
  "W": ["#.....#", "#.....#", "#.....#", "#.....#", "#..#..#", "#..#..#", "#.#.#.#", "##...##", "#.....#"],
  "X": ["#...#", "#...#", ".#.#.", ".#.#.", "..#..", ".#.#.", ".#.#.", "#...#", "#...#"],
  "Y": ["#...#", "#...#", ".#.#.", ".#.#.", "..#..", "..#..", "..#..", "..#..", "..#.."],
  "Z": ["######", ".....#", "....#.", "...#..", "..#...", ".#....", "#.....", "#.....", "######"],
  "a": [".....", ".....", ".....", ".###.", "....#", ".####", "#...#", "#...#", ".####"],
  "b": ["#....", "#....", "#....", "####.", "#...#", "#...#", "#...#", "#...#", "####."],
  "c": [".....", ".....", ".....", ".###.", "#...#", "#....", "#....", "#...#", ".###."],
  "d": ["....#", "....#", "....#", ".####", "#...#", "#...#", "#...#", "#...#", ".####"],
  "e": [".....", ".....", ".....", ".###.", "#...#", "#####", "#....", "#...#", ".###."],
  "f": ["..##", ".#..", ".#..", "###.", ".#..", ".#..", ".#..", ".#..", ".#.."],
  "g": [".....", ".....", ".....", ".####", "#...#", "#...#", "#...#", "#...#", ".####", "....#", ".###."],
  "h": ["#....", "#....", "#....", "####.", "#...#", "#...#", "#...#", "#...#", "#...#"],
  "i": ["..", "#.", "..", "#.", "#.", "#.", "#.", "#.", "##"],
  "j": ["...", "..#", "...", "..#", "..#", "..#", "..#", "..#", "..#", "..#", "##."],
  "k": ["#....", "#....", "#....", "#...#", "#..#.", "#.#..", "###..", "#..#.", "#...#"],
  "l": ["#.", "#.", "#.", "#.", "#.", "#.", "#.", "#.", "##"],
  "m": [".......", ".......", ".......", "#######", "#..#..#", "#..#..#", "#..#..#", "#..#..#", "#..#..#"],
  "n": [".....", ".....", ".....", "####.", "#...#", "#...#", "#...#", "#...#", "#...#"],
  "o": [".....", ".....", ".....", ".###.", "#...#", "#...#", "#...#", "#...#", ".###."],
  "p": [".....", ".....", ".....", "####.", "#...#", "#...#", "#...#", "#...#", "####.", "#....", "#...."],
  "q": [".....", ".....", ".....", ".####", "#...#", "#...#", "#...#", "#...#", ".####", "....#", "....#"],
  "r": ["....", "....", "....", "#.##", "##..", "#...", "#...", "#...", "#..."],
  "s": [".....", ".....", ".....", ".####", "#....", "#....", ".###.", "....#", "####."],
  "t": ["....", ".#..", ".#..", "####", ".#..", ".#..", ".#..", ".#..", "..##"],
  "u": [".....", ".....", ".....", "#...#", "#...#", "#...#", "#...#", "#...#", ".####"],
  "v": [".....", ".....", ".....", "#...#", "#...#", "#...#", ".#.#.", ".#.#.", "..#.."],
  "w": [".......", ".......", ".......", "#.....#", "#.....#", "#..#..#", "#..#..#", "#.#.#.#", ".#...#."],
  "x": [".....", ".....", ".....", "#...#", ".#.#.", "..#..", "..#..", ".#.#.", "#...#"],
  "y": [".....", ".....", ".....", "#...#", "#...#", "#...#", "#...#", "#...#", ".####", "....#", "####."],
  "z": [".....", ".....", ".....", "#####", "...#.", "..#..", "..#..", ".#...", "#####"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "##", "##"],
  ",": ["..", "..", "..", "..", "..", "..", "..", "##", "##", ".#", "#."],
  ":": ["..", "..", "..", "##", "##", "..", "..", "##", "##"],
  ";": ["..", "..", "..", "##", "##", "..", "..", "##", "##", ".#", "#."],
  "/": ["....#", "....#", "...#.", "...#.", "..#..", "..#..", ".#...", ".#...", "#...."],
  "+": [".....", ".....", ".....", "..#..", "..#..", "#####", "..#..", "..#.."],
  "-": [".....", ".....", ".....", ".....", ".....", "#####"],
  "=": [".....", ".....", ".....", ".....", "#####", ".....", "#####"],
  "%": ["##...#.", "##..#..", "....#..", "...#...", "..#....", ".#.....", ".#...##", "#....##", ".....##"],
  "'": ["#", "#", "#"],
  "\"": ["#.#", "#.#", "#.#"],
  "!": ["#", "#", "#", "#", "#", "#", "#", ".", "#"],
  "(": ["..#", ".#.", "#..", "#..", "#..", "#..", "#..", ".#.", "..#"],
  ")": ["#..", ".#.", "..#", "..#", "..#", "..#", "..#", ".#.", "#.."],
  "*": ["...", "#.#", ".#.", "#.#"],
  "<": ["...", "...", "...", "..#", ".#.", "#..", ".#.", "..#"],
  ">": ["...", "...", "...", "#..", ".#.", "..#", ".#.", "#.."],
  "·": [".", ".", ".", ".", "#"],
  "—": ["......", "......", "......", "......", "######"],
  "?": [".###.", "#...#", "....#", "...#.", "..#..", "..#..", ".....", ".....", "..#.."],
};

/**
 * The display face: cap 11, x-height 7, TWO-pixel stems, descent 3.
 *
 * This is the classic's `bigfont` metric and it is the weight the build has never
 * had. A heading set in the seven-row face at magnification 3 is the same
 * hairline stroke blown up; a display face is a different drawing, with stems
 * twice the weight of its counters, which is why the original's screen titles
 * hold at any size without an outline under them.
 */
const DISPLAY_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["......", "......", "......", "......", "......", "......", "......", "......", "......", "......", "......"],
  "0": [".#####.", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", ".#####."],
  "1": ["...##..", "..###..", ".####..", "...##..", "...##..", "...##..", "...##..", "...##..", "...##..", "...##..", "#######"],
  "2": [".#####.", "##...##", "##...##", ".....##", "....##.", "...##..", "..##...", ".##....", "##.....", "##.....", "#######"],
  "3": [".#####.", "##...##", ".....##", ".....##", "..####.", ".....##", ".....##", ".....##", "##...##", "##...##", ".#####."],
  "4": ["....##.", "...###.", "..####.", ".##.##.", "##..##.", "#######", "#######", "....##.", "....##.", "....##.", "....##."],
  "5": ["#######", "##.....", "##.....", "##.....", "######.", ".....##", ".....##", ".....##", "##...##", "##...##", ".#####."],
  "6": ["..####.", ".##....", "##.....", "##.....", "######.", "##...##", "##...##", "##...##", "##...##", "##...##", ".#####."],
  "7": ["#######", "#######", ".....##", "....##.", "....##.", "...##..", "...##..", "..##...", "..##...", ".##....", ".##...."],
  "8": [".#####.", "##...##", "##...##", "##...##", ".#####.", "##...##", "##...##", "##...##", "##...##", "##...##", ".#####."],
  "9": [".#####.", "##...##", "##...##", "##...##", "##...##", ".######", ".....##", ".....##", "....##.", "...##..", ".###..."],
  "A": ["...##...", "...##...", "..####..", "..####..", ".##..##.", ".##..##.", "########", "########", "##....##", "##....##", "##....##"],
  "B": ["######..", "##...##.", "##...##.", "##...##.", "######..", "######..", "##...##.", "##...##.", "##...##.", "##...##.", "######.."],
  "C": ["..#####.", ".##...##", "##.....#", "##......", "##......", "##......", "##......", "##......", "##.....#", ".##...##", "..#####."],
  "D": ["######..", "##..##..", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##..##..", "######.."],
  "E": ["########", "##......", "##......", "##......", "######..", "######..", "##......", "##......", "##......", "##......", "########"],
  "F": ["########", "##......", "##......", "##......", "######..", "######..", "##......", "##......", "##......", "##......", "##......"],
  "G": ["..#####.", ".##...##", "##......", "##......", "##......", "##..####", "##....##", "##....##", "##....##", ".##..##.", "..#####."],
  "H": ["##....##", "##....##", "##....##", "##....##", "########", "########", "##....##", "##....##", "##....##", "##....##", "##....##"],
  "I": ["####", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "####"],
  "J": ["..#####", "....##.", "....##.", "....##.", "....##.", "....##.", "....##.", "....##.", "##..##.", "##..##.", ".####.."],
  "K": ["##....##", "##...##.", "##..##..", "##.##...", "####....", "####....", "##.##...", "##..##..", "##...##.", "##....##", "##....##"],
  "L": ["##......", "##......", "##......", "##......", "##......", "##......", "##......", "##......", "##......", "##......", "########"],
  "M": ["##......##", "###....###", "####..####", "##.####.##", "##..##..##", "##......##", "##......##", "##......##", "##......##", "##......##", "##......##"],
  "N": ["##....##", "###...##", "####..##", "##.##.##", "##..####", "##...###", "##....##", "##....##", "##....##", "##....##", "##....##"],
  "O": ["..####..", ".##..##.", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", ".##..##.", "..####.."],
  "P": ["######..", "##...##.", "##...##.", "##...##.", "##...##.", "######..", "##......", "##......", "##......", "##......", "##......"],
  "Q": ["..####..", ".##..##.", "##....##", "##....##", "##....##", "##....##", "##....##", "##.##.##", "##..####", ".##..##.", "..####.#"],
  "R": ["######..", "##...##.", "##...##.", "##...##.", "##...##.", "######..", "##.##...", "##..##..", "##...##.", "##....##", "##....##"],
  "S": ["..#####.", ".##...##", "##......", "##......", ".####...", "...###..", ".....##.", "......##", "##....##", ".##..##.", "..####.."],
  "T": ["########", "########", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##..."],
  "U": ["##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", ".##..##.", "..####.."],
  "V": ["##....##", "##....##", "##....##", "##....##", "##....##", "##....##", ".##..##.", ".##..##.", "..####..", "..####..", "...##..."],
  "W": ["##......##", "##......##", "##......##", "##......##", "##......##", "##..##..##", "##..##..##", "##.####.##", "####..####", "###....###", "##......##"],
  "X": ["##....##", "##....##", ".##..##.", ".##..##.", "..####..", "...##...", "..####..", ".##..##.", ".##..##.", "##....##", "##....##"],
  "Y": ["##....##", "##....##", ".##..##.", ".##..##.", "..####..", "...##...", "...##...", "...##...", "...##...", "...##...", "...##..."],
  "Z": ["########", "########", ".....##.", "....##..", "....##..", "...##...", "..##....", "..##....", ".##.....", "########", "########"],
  "a": [".......", ".......", ".......", ".......", ".#####.", "##...##", ".....##", ".#####.", "##...##", "##...##", ".#####."],
  "b": ["##.....", "##.....", "##.....", "##.....", "######.", "##...##", "##...##", "##...##", "##...##", "##...##", "######."],
  "c": [".......", ".......", ".......", ".......", ".#####.", "##...##", "##.....", "##.....", "##.....", "##...##", ".#####."],
  "d": [".....##", ".....##", ".....##", ".....##", ".######", "##...##", "##...##", "##...##", "##...##", "##...##", ".######"],
  "e": [".......", ".......", ".......", ".......", ".#####.", "##...##", "##...##", "#######", "##.....", "##...##", ".#####."],
  "f": ["...###", "..##..", "..##..", "..##..", "######", "..##..", "..##..", "..##..", "..##..", "..##..", "..##.."],
  "g": [".......", ".......", ".......", ".......", ".######", "##...##", "##...##", "##...##", "##...##", "##...##", ".######", ".....##", "##...##", ".#####."],
  "h": ["##.....", "##.....", "##.....", "##.....", "######.", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##"],
  "i": ["...", "##.", "##.", "...", "##.", "##.", "##.", "##.", "##.", "##.", "###"],
  "j": [".....", "...##", "...##", ".....", "...##", "...##", "...##", "...##", "...##", "...##", "...##", "...##", "##.##", ".###."],
  "k": ["##.....", "##.....", "##.....", "##.....", "##...##", "##..##.", "##.##..", "#####..", "##.##..", "##..##.", "##...##"],
  "l": ["##.", "##.", "##.", "##.", "##.", "##.", "##.", "##.", "##.", "##.", "###"],
  "m": ["...........", "...........", "...........", "...........", "###########", "##..##...##", "##..##...##", "##..##...##", "##..##...##", "##..##...##", "##..##...##"],
  "n": [".......", ".......", ".......", ".......", "######.", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##"],
  "o": [".......", ".......", ".......", ".......", ".#####.", "##...##", "##...##", "##...##", "##...##", "##...##", ".#####."],
  "p": [".......", ".......", ".......", ".......", "######.", "##...##", "##...##", "##...##", "##...##", "##...##", "######.", "##.....", "##.....", "##....."],
  "q": [".......", ".......", ".......", ".......", ".######", "##...##", "##...##", "##...##", "##...##", "##...##", ".######", ".....##", ".....##", ".....##"],
  "r": ["......", "......", "......", "......", "##.###", "#####.", "##....", "##....", "##....", "##....", "##...."],
  "s": [".......", ".......", ".......", ".......", ".######", "##.....", "##.....", ".#####.", ".....##", ".....##", "######."],
  "t": ["......", "..##..", "..##..", "..##..", "######", "..##..", "..##..", "..##..", "..##..", "..##..", "...###"],
  "u": [".......", ".......", ".......", ".......", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", ".######"],
  "v": [".......", ".......", ".......", ".......", "##...##", "##...##", "##...##", ".##.##.", ".##.##.", "..###..", "...##.."],
  "w": ["...........", "...........", "...........", "...........", "##.......##", "##.......##", "##...#...##", "##..###..##", "##.##.##.##", "####...####", ".##.....##."],
  "x": [".......", ".......", ".......", ".......", "##...##", "##...##", ".##.##.", "..###..", ".##.##.", "##...##", "##...##"],
  "y": [".......", ".......", ".......", ".......", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", ".######", ".....##", "##...##", ".#####."],
  "z": [".......", ".......", ".......", ".......", "#######", "....##.", "...##..", "..##...", ".##....", "##.....", "#######"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "..", "..", "##", "##"],
  ",": ["...", "...", "...", "...", "...", "...", "...", "...", "...", ".##", ".##", ".##", "##.", "##."],
  ":": ["..", "..", "..", "..", "##", "##", "..", "..", "..", "##", "##"],
  ";": ["...", "...", "...", "...", ".##", ".##", "...", "...", "...", ".##", ".##", ".##", "##.", "##."],
  "/": [".....##", ".....##", "....##.", "....##.", "...##..", "...##..", "..##...", "..##...", ".##....", ".##....", "##....."],
  "+": [".......", ".......", ".......", ".......", "...##..", "...##..", "#######", "#######", "...##..", "...##.."],
  "-": [".......", ".......", ".......", ".......", ".......", ".......", "#######", "#######"],
  "=": [".......", ".......", ".......", ".......", ".......", "#######", "#######", ".......", "#######", "#######"],
  "%": [".........", ".........", "###....##", "###...##.", "###..##..", "....##...", "...##....", "..##.....", ".##...###", "##....###", "......###"],
  "'": ["##", "##", "##"],
  "\"": ["##.##", "##.##", "##.##"],
  "!": ["##", "##", "##", "##", "##", "##", "##", "##", "..", "##", "##"],
  "(": ["..##", ".##.", "##..", "##..", "##..", "##..", "##..", "##..", "##..", ".##.", "..##"],
  ")": ["##..", ".##.", "..##", "..##", "..##", "..##", "..##", "..##", "..##", ".##.", "##.."],
  "*": [".......", "##...##", ".##.##.", "#######", ".##.##.", "##...##"],
  "<": ["......", "......", "......", "....##", "..##..", "##....", "##....", "..##..", "....##"],
  ">": ["......", "......", "......", "##....", "..##..", "....##", "....##", "..##..", "##...."],
  "·": ["..", "..", "..", "..", "..", "##", "##"],
  "—": [".........", ".........", ".........", ".........", ".........", "#########", "#########"],
  "?": [".#####.", "##...##", ".....##", "....##.", "...##..", "..##...", "..##...", ".......", ".......", "..##...", "..##..."],
};

/**
 * The tall face: cap 11, x-height 8, two-pixel stems, descent 3, at the text
 * face's widths (digits 6, capitals 6-8).
 *
 * GENERATED, unlike the three above, and kept in the same picture form so it
 * is edited the same way. Source: Nimbus Sans Narrow Bold (URW, installed
 * under `/usr/share/fonts/opentype/urw-base35`) rendered by FreeType in 1-bit
 * mode at 15px, the size at which its `H` stands exactly `MIN_DEVICE_CAP`
 * rows; clipped to the cap row, digits padded to one shared width, space four
 * columns like the text face's. The key set is the text face's exactly, so a
 * reading promoted into this face never changes its wording. Regenerate with
 * `gen_tall.py` (the generator is short: render each TEXT_PICTURES key, crop
 * to ink, pad digits) - or edit a picture by hand; the picture IS the data.
 */
const TALL_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "...."],
  "0": ["..###.", ".#####", ".#..##", ".#...#", "##...#", "##...#", "##...#", "##...#", ".#..##", ".#####", "..###."],
  "1": ["...##.", "...##.", ".####.", ".####.", "...##.", "...##.", "...##.", "...##.", "...##.", "...##.", "...##."],
  "2": ["..###.", ".#####", ".#..##", "##...#", "....##", "....##", "...##.", "..##..", ".##...", "######", "######"],
  "3": ["..###.", ".#####", ".#..##", "....##", "...###", "...###", "....##", "##...#", "##..##", ".#####", "..###."],
  "4": ["...##.", "...##.", "..###.", "..#.#.", ".##.#.", ".#..#.", "##..#.", "######", "######", "....#.", "....#."],
  "5": [".#####", ".#####", ".#....", ".#.##.", ".#####", ".#..##", ".....#", ".....#", "##..##", ".#####", "..###."],
  "6": ["..###.", ".#####", ".#..##", ".#....", "#####.", "######", "##..##", "##...#", ".#..##", ".#####", "..###."],
  "7": ["######", "######", "....##", "....#.", "...##.", "...#..", "..##..", "..##..", "..##..", "..#...", "..#..."],
  "8": ["..###.", ".#####", ".#..##", ".#..##", ".#####", ".#####", ".#..##", "##...#", "##...#", ".#####", "..###."],
  "9": ["..###.", ".#####", "##..##", "##...#", "##..##", ".#####", "..##.#", ".....#", ".#..##", ".#####", "..###."],
  "A": ["...###..", "...###..", "...###..", "..##.##.", "..##.##.", "..##.##.", "..#..##.", ".#######", ".#######", ".##...##", "##....##"],
  "B": ["######.", "#######", "##...##", "##...##", "######.", "#######", "##...##", "##...##", "##...##", "#######", "######."],
  "C": ["..####.", ".######", "##...##", "##....#", "#......", "#......", "#......", "##...##", "##...##", ".######", "..###.."],
  "D": ["######.", "#######", "##...##", "##...##", "##....#", "##....#", "##...##", "##...##", "##...##", "######.", "#####.."],
  "E": ["######.", "######.", "##.....", "##.....", "######.", "######.", "##.....", "##.....", "##.....", "#######", "#######"],
  "F": ["######", "######", "##....", "##....", "######", "######", "##....", "##....", "##....", "##....", "##...."],
  "G": ["..####..", ".######.", "##...###", "##....##", "#.......", "#...####", "#...####", "##....##", "##...###", ".#######", "..###.##"],
  "H": ["##...##", "##...##", "##...##", "##...##", "#######", "#######", "##...##", "##...##", "##...##", "##...##", "##...##"],
  "I": ["##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##"],
  "J": ["....##", "....##", "....##", "....##", "....##", "....##", "....##", "##..##", "##..##", ".#####", ".####."],
  "K": ["##...##.", "##..##..", "##.###..", "##.##...", "####....", "#####...", "#####...", "##.###..", "##..##..", "##...##.", "##...###"],
  "L": ["##....", "##....", "##....", "##....", "##....", "##....", "##....", "##....", "##....", "######", "######"],
  "M": ["###...##", "###..###", "###..###", "###..#.#", "###..#.#", "####.#.#", "####.#.#", "##.###.#", "##.##..#", "##.##..#", "##.##..#"],
  "N": ["##...##", "##...##", "###..##", "###..##", "####.##", "##.#.##", "##.####", "##..###", "##..###", "##...##", "##...##"],
  "O": ["..####..", ".######.", "##...##.", "##....##", "#.....##", "#.....##", "#.....##", "##....##", "##...##.", ".######.", "..####.."],
  "P": ["######.", "######.", "##...##", "##...##", "##...##", "######.", "#####..", "##.....", "##.....", "##.....", "##....."],
  "Q": ["..####..", ".######.", "##...##.", "##....##", "#.....##", "#.....##", "#.....##", "##..####", "##..###.", ".######.", "..######"],
  "R": ["######.", "#######", "##...##", "##...##", "##...##", "######.", "#######", "##...##", "##...##", "##...##", "##...##"],
  "S": ["..####..", ".######.", ".#...##.", ".#......", ".###....", "..#####.", "....####", "##....##", ".##...##", ".######.", "..####.."],
  "T": ["#######", "#######", "...##..", "...##..", "...##..", "...##..", "...##..", "...##..", "...##..", "...##..", "...##.."],
  "U": ["##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", "######.", ".####.."],
  "V": ["##....##", ".##...##", ".##..##.", ".##..##.", ".##..##.", "..#..#..", "..##.#..", "..####..", "...###..", "...##...", "...##..."],
  "W": ["##...##..##", ".#...##..##", ".##.###..##", ".##.###..##", ".##.#.##.#.", ".##.#.##.#.", "..#.#.####.", "..#.#..###.", "..###..###.", "..###..##..", "...#...##.."],
  "X": [".##..###", ".##..##.", "..##.##.", "..####..", "...###..", "...##...", "..####..", "..####..", ".###.##.", ".##..##.", "###...##"],
  "Y": ["###...##", ".##..##.", ".##..##.", "..##.##.", "..####..", "...###..", "...##...", "...##...", "...##...", "...##...", "...##..."],
  "Z": ["#######", "#######", "....###", "....##.", "...##..", "...##..", "..##...", ".###...", ".##....", "#######", "#######"],
  "a": ["......", "......", "......", "..###.", ".#####", ".#..##", "..####", ".##.##", "##..##", "######", ".###.#"],
  "b": ["#.....", "#.....", "#.....", "#.###.", "######", "##..##", "#...##", "#...##", "##..##", "######", "#.###."],
  "c": ["......", "......", "......", "..###.", ".#####", ".#...#", "##....", "##....", ".#...#", ".#####", "..###."],
  "d": [".....##", ".....##", ".....##", "..##.##", ".######", ".##..##", "##...##", "##...##", ".#...##", ".######", "..#####"],
  "e": ["......", "......", "......", "..###.", ".#####", "##...#", "######", "##....", "##...#", ".#####", "..###."],
  "f": ["..##", ".###", ".##.", "####", "####", ".##.", ".##.", ".##.", ".##.", ".##.", ".##."],
  "g": [".......", ".......", ".......", "..#####", ".######", ".##..##", ".#...##", "##...##", ".##..##", ".######", "..#####", ".....##", ".#####.", "..####."],
  "h": ["##....", "##....", "##....", "#####.", "######", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##"],
  "i": ["##", "##", "..", "##", "##", "##", "##", "##", "##", "##", "##"],
  "j": [".##", ".##", "...", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", "###", "##."],
  "k": ["##....", "##....", "##....", "##.##.", "##.##.", "####..", "####..", "####..", "##.##.", "##.##.", "##..##"],
  "l": ["##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##"],
  "m": [".........", ".........", "#.###.##.", "#########", "##..#..##", "#...#..##", "#...#..##", "#...#..##", "#...#..##", "#...#..##", "........."],
  "n": ["......", "......", "#.###.", "######", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##", "......"],
  "o": [".......", ".......", ".......", "..####.", ".#####.", ".#...##", "##...##", "##...##", ".#...##", ".#####.", "..####."],
  "p": ["......", "......", "......", "#.###.", "######", "##..##", "#...##", "#...##", "##..##", "######", "#.###.", "#.....", "#.....", "#....."],
  "q": [".......", ".......", ".......", "..#####", ".######", ".##..##", "##...##", "##...##", ".#...##", ".######", "..#####", ".....##", ".....##", ".....##"],
  "r": ["....", "....", "#.##", "####", "##..", "##..", "#...", "#...", "#...", "#...", "...."],
  "s": ["......", "......", "......", "..###.", ".#####", ".#..##", ".####.", "..####", "##...#", ".#####", "..###."],
  "t": [".##.", ".##.", "####", "####", ".##.", ".##.", ".##.", ".##.", ".###", ".###", "...."],
  "u": ["......", "......", "......", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##", "######", ".#####"],
  "v": [".......", ".......", ".......", "##...##", ".#..##.", ".##.##.", ".##.##.", ".##.#..", "..#.#..", "..###..", "..##..."],
  "w": [".........", ".........", ".........", "##..##..#", "##..##.##", ".#..##.##", ".#####.##", ".###.#.#.", ".###.###.", "..##..##.", "..##..##."],
  "x": ["......", "......", "......", ".##.##", ".##.##", "..###.", "..###.", "..###.", "..###.", ".##.##", "##..##"],
  "y": ["......", "......", "......", "##...#", ".#..##", ".##.##", ".##.##", ".##.#.", "..#.#.", "..###.", "..##..", "..##..", ".###..", ".##..."],
  "z": ["......", "......", "......", "######", "######", "...##.", "..##..", "..##..", ".##...", "######", "######"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "..", "##", "##", ".."],
  ",": ["..", "..", "..", "..", "..", "..", "..", "..", "##", "##", ".#", "#."],
  ":": ["..", "..", "..", "##", "##", "..", "..", "..", "..", "##", "##"],
  ";": ["..", "..", "..", "##", "##", "..", "..", "..", "..", "##", "##", ".#", ".#"],
  "/": ["..##", "..#.", "..#.", ".##.", ".##.", ".#..", ".#..", "##..", "##..", "#...", "#..."],
  "+": [".......", ".......", ".......", "...#...", "...#...", "...#...", "#######", "#######", "...#...", "...#...", "...#..."],
  "-": ["....", "....", "....", "....", "....", "....", "####", "####", "....", "....", "...."],
  "=": [".......", ".......", ".......", ".......", ".......", "#######", "#######", ".......", "#######", "#######", "......."],
  "%": [".##....#...", "##.#...#...", "##.#..#....", "##.#..#....", "##.#.#.....", ".##..#.###.", "....#..#.#.", "....#..#.##", "....#..#.##", "...#...#.#.", "...#...###."],
  "'": ["#", "#", "#", "#", ".", ".", ".", ".", ".", ".", "."],
  "\"": ["#.##", "#.##", "#.##", "#.##", "....", "....", "....", "....", "....", "....", "...."],
  "!": ["##", "##", "##", "##", "##", "##", "##", ".#", "..", "##", "##"],
  "(": ["..##", "..#.", ".##.", ".##.", ".#..", ".#..", "##..", "##..", "##..", ".#..", ".##.", ".##.", "..#.", "..##"],
  ")": ["##..", ".#..", ".##.", ".##.", "..#.", "..##", "..##", "..##", "..##", "..#.", "..#.", ".##.", ".#..", ".#.."],
  "*": [".#.", ".#.", "###", "###", "#.#", "...", "...", "...", "...", "...", "..."],
  "<": [".......", ".......", "......#", "....###", "..###..", "###....", "###....", "..###..", "....###", "......#", "......."],
  ">": [".......", ".......", "#......", "###....", "..###..", "....###", "....###", "..###..", "###....", "#......", "......."],
  "\u00b7": [".", ".", ".", ".", ".", ".", "#", "#", ".", ".", "."],
  "\u2014": ["............", "............", "............", "............", "............", "............", "############", "############", "............", "............", "............"],
  "?": [".####.", "#####.", "##..##", "#...##", "....##", "...##.", "..##..", "..##..", "......", "..##..", "..##.."],
};

/**
 * The caption face: cap 15, x-height 11, two-pixel stems, descenders four rows
 * under the baseline. Generated from Nimbus Sans Narrow Bold rendered by
 * FreeType in 1-bit mode at 20px (`gen_caption.py`: render each TEXT_PICTURES
 * key, crop to ink, clip above the cap row, pad digits to one width, space
 * five columns) - the picture IS the data, as with the tall face.
 *
 * It exists for the 1080-tall window. `deviceCapFloor` in `devicePixels.ts`
 * holds every reading there to 15 device rows - the HD Edition stamps its
 * captions at 16 - and without a face that stands 15 rows at magnification 1
 * the promotion lands on the text face at magnification 2: 18 rows, twice
 * as wide as what the sidebar's grooves and the arena's stack cards were
 * measured for. The narrow cut is deliberate for the same reason: `Month 1,
 * Week 1, Day 1` sets 180 columns here against 220 in the regular width.
 * On a 768-tall window the floor is 11 and this face is never chosen.
 */
const CAPTION_PICTURES: Readonly<Record<string, Picture>> = {
  " ": [".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", "....."],
  "0": [".........", "..###....", ".#####...", "###.###..", "##...##..", "##...##..", "##...###.", "##...###.", "##...###.", "##...###.", "##...##..", "##...##..", "###.###..", ".#####...", "..###...."],
  "1": [".....##..", ".....##..", "..#####..", "..#####..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", ".....##..", "........."],
  "2": [".........", "..####...", ".######..", ".###.###.", ".##...##.", ".##...##.", "......##.", ".....###.", "....###..", "...###...", "..###....", ".###.....", ".##......", ".#######.", "########."],
  "3": [".........", "..####...", ".######..", ".##..###.", ".##...##.", "......##.", ".....###.", "...####..", "...#####.", "......##.", "###...##.", ".##...##.", ".##..###.", ".#######.", "..####..."],
  "4": ["....###..", "....###..", "...####..", "...####..", "..##.##..", "..##.##..", ".##..##..", ".##..##..", "##...##..", "#########", "#########", ".....##..", ".....##..", ".....##..", "........."],
  "5": [".........", "..######.", ".#######.", ".##......", ".##......", ".##......", ".######..", ".#######.", ".##...##.", "......##.", "......##.", "###...##.", ".##..###.", ".######..", "..####..."],
  "6": [".........", "..####...", ".######..", "###..##..", "##.......", "##.......", "######...", "#######..", "##...##..", "##...##..", "##...###.", "##...##..", "###.###..", ".######..", "..###...."],
  "7": [".........", "#########", "#########", "......##.", ".....###.", ".....##..", "....###..", "....##...", "...###...", "...##....", "...##....", "...##....", "..###....", "..###....", "..##....."],
  "8": [".........", "..###....", ".######..", "###.###..", "##...##..", "##...##..", "###..##..", ".#####...", "#######..", "##...##..", "##...###.", "##...###.", "###.###..", "#######..", ".#####..."],
  "9": [".........", "..###....", "######...", "###.###..", "##...##..", "##...##..", "##...###.", "##...###.", "########.", ".######..", ".....##..", "##...##..", "##..###..", "######...", ".####...."],
  "A": ["....###....", "....####...", "....####...", "....####...", "...######..", "...##.###..", "...##..##..", "..###..##..", "..###..###.", "..##...###.", "..########.", ".##########", ".###....###", ".##.....###", "###......##"],
  "B": ["########..", "#########.", "###...###.", "###....###", "###....###", "###....##.", "#########.", "#########.", "###...####", "###....###", "###.....##", "###....###", "###...####", "#########.", "########.."],
  "C": ["...####...", "..#######.", ".########.", "###....###", "###.....##", "##........", "##........", "##........", "##........", "##........", "###.....##", "###....###", ".########.", "..#######.", "...####..."],
  "D": ["########..", "#########.", "###..####.", "###....###", "###....###", "###.....##", "###.....##", "###.....##", "###.....##", "###.....##", "###....###", "###....###", "###..####.", "#########.", "#######..."],
  "E": ["#########", "#########", "#########", "###......", "###......", "###......", "########.", "########.", "########.", "###......", "###......", "###......", "#########", "#########", "#########"],
  "F": ["#########", "#########", "#########", "###......", "###......", "###......", "########.", "########.", "########.", "###......", "###......", "###......", "###......", "###......", "###......"],
  "G": ["...#####...", "..#######..", ".####.####.", "###.....##.", "###.....###", "##.........", "##.........", "##....#####", "##....#####", "##.......##", "###.....###", "###.....###", ".##########", "..######.##", "...####..##"],
  "H": ["###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "##########", "##########", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###"],
  "I": ["###", "###", "###", "###", "###", "###", "###", "###", "###", "###", "###", "###", "###", "###", "###"],
  "J": [".....###", ".....###", ".....###", ".....###", ".....###", ".....###", ".....###", ".....###", ".....###", "###..###", "###..###", "###..###", ".###.###", ".######.", "..####.."],
  "K": ["###....###.", "###...###..", "###...###..", "###..###...", "###.###....", "######.....", "######.....", "#######....", "#######....", "###..###...", "###..###...", "###...###..", "###...###..", "###....###.", "###....####"],
  "L": ["###......", "###......", "###......", "###......", "###......", "###......", "###......", "###......", "###......", "###......", "###......", "###......", "#########", "#########", "#########"],
  "M": ["####....####", "####....####", "####....####", "##.#...#####", "##.##..#####", "##.##..#####", "##.##..#####", "##.##..#.###", "##.##.##.###", "##..#.##.###", "##..####.###", "##..####.###", "##..###..###", "##..###..###", "##...##..###"],
  "N": ["###....###", "###....###", "####...###", "####...###", "##.##..###", "##.##..###", "##.###.###", "##..##.###", "##..######", "##...#####", "##...#####", "##....####", "##....####", "##.....###", "##.....###"],
  "O": ["...#####...", "..#######..", ".#########.", "###....###.", "###.....###", "##......###", "##.......##", "##.......##", "##.......##", "##......###", "###.....###", "###....###.", ".#########.", "..#######..", "...#####..."],
  "P": ["#######..", "########.", "###..####", "###....##", "###....##", "###....##", "###...###", "#########", "########.", "###......", "###......", "###......", "###......", "###......", "###......"],
  "Q": ["...#####...", "..#######..", ".#########.", "###....###.", "###.....###", "##......###", "##.......##", "##.......##", "##.......##", "##....#.###", "###...#####", "###...####.", ".#########.", "..#########", "...#####.##"],
  "R": ["########..", "#########.", "###...####", "###....###", "###....###", "###....###", "###...###.", "########..", "#########.", "###....##.", "###....###", "###....###", "###....###", "###....###", "###....###"],
  "S": ["..#####..", ".#######.", "####.###.", "##....###", "##.....##", "###......", "######...", ".#######.", "...######", "......###", "##.....##", "##.....##", "####.####", ".#######.", "..#####.."],
  "T": ["##########", "##########", "##########", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##...."],
  "U": ["###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....###", "###....##.", ".########.", ".########.", "...####..."],
  "V": ["###.....##", ".##.....##", ".###...###", ".###...###", ".###...##.", "..##...##.", "..##...##.", "..###.###.", "...##.##..", "...##.##..", "...##.##..", "...#####..", "....###...", "....###...", "....###..."],
  "W": ["###...###....##", ".##...###...###", ".##...###...###", ".###..####..##.", ".###..####..##.", ".###..#.##..##.", "..##.##.##..##.", "..##.##.##.###.", "..##.##.##.##..", "..#####..####..", "...####..####..", "...###...####..", "...###...###...", "...###...###...", "...###...###..."],
  "X": [".###...###", ".###...###", "..##..###.", "..###.###.", "...##.##..", "...#####..", "....###...", "....###...", "...####...", "...#####..", "..###.##..", "..###.###.", ".###...##.", ".###...###", "###....###"],
  "Y": ["###.....##", ".###...###", ".###...###", "..##...##.", "..###.###.", "...##.##..", "...#####..", "...#####..", "....###...", "....###...", "....###...", "....###...", "....###...", "....###...", "....###..."],
  "Z": [".#########", ".#########", ".#########", "......###.", ".....###..", ".....###..", "....###...", "...###....", "...###....", "..###.....", "..###.....", ".###......", "##########", "##########", "##########"],
  "a": ["........", "........", "........", "........", "..#####.", ".#######", ".##..###", ".##...##", "....####", ".#######", ".##...##", "###...##", ".##..###", ".#######", "..###.##"],
  "b": ["##......", "##......", "##......", "##......", "##.###..", "#######.", "####.###", "###...##", "##....##", "##....##", "##....##", "###...##", "###..###", "#######.", "##.###.."],
  "c": ["........", "........", "........", "........", "..####..", ".######.", "###.###.", "##...###", "##......", "##......", "##......", "##...##.", "###.###.", ".######.", "..####.."],
  "d": ["......##", "......##", "......##", "......##", "..###.##", ".#######", "###.####", "##...###", "##....##", "##....##", "##....##", "##....##", "###..###", ".#######", "..###.##"],
  "e": [".........", ".........", ".........", ".........", "...####..", ".#######.", ".###.###.", ".##...##.", "#########", "#########", "###......", ".##...###", ".###..##.", ".#######.", "...####.."],
  "f": ["..###", "..###", ".###.", ".###.", "#####", "#####", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###."],
  "g": ["........", "........", "........", "........", "..###.##", ".#######", "###.####", "##...###", "##....##", "##....##", "##....##", "##...###", "###..###", ".#######", "..###.##", "......##", "###..###", "#######.", ".#####.."],
  "h": ["##......", "##......", "##......", "##......", "##.####.", "#######.", "####.###", "###...##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##"],
  "i": ["##", "##", "##", "..", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##"],
  "j": [".##", ".##", ".##", "...", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", "###", "###"],
  "k": ["##......", "##......", "##......", "##......", "##...###", "##..###.", "##..##..", "##.###..", "#####...", "######..", "######..", "##..###.", "##..###.", "##...###", "##...###"],
  "l": ["##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##", "##"],
  "m": [".............", ".............", ".............", "##.###..###..", "############.", "###.####.###.", "##...###..###", "##...##...###", "##...##...###", "##...##...###", "##...##...###", "##...##...###", "##...##...###", "##...##...###", "............."],
  "n": ["........", "........", "........", "##.####.", "#######.", "####.###", "###...##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "........"],
  "o": ["........", "........", "........", "........", "..####..", ".######.", "###..###", "##....##", "##....##", "##....##", "##....##", "##....##", "###..###", ".######.", "..####.."],
  "p": ["........", "........", "........", "........", "##.###..", "#######.", "####.###", "###...##", "##....##", "##....##", "##....##", "###...##", "###..###", "#######.", "##.###..", "##......", "##......", "##......", "##......"],
  "q": ["........", "........", "........", "........", "..###.##", ".#######", "###.####", "##...###", "##....##", "##....##", "##....##", "##...###", "###..###", ".#######", "..###.##", "......##", "......##", "......##", "......##"],
  "r": [".....", ".....", ".....", "##.##", "#####", "#####", "###..", "##...", "##...", "##...", "##...", "##...", "##...", "##...", "....."],
  "s": ["........", "........", "........", "........", ".#####..", "#######.", "##...##.", "##......", "#####...", ".######.", "...####.", "##...###", "##...##.", "#######.", ".#####.."],
  "t": [".....", ".###.", ".###.", ".###.", "#####", "#####", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".####", "..###"],
  "u": ["........", "........", "........", "........", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##...###", "###..###", ".#######", ".####.##"],
  "v": [".........", ".........", ".........", ".........", "###...###", ".##...##.", ".##...##.", ".###..##.", ".###.###.", "..##.##..", "..##.##..", "..##.##..", "...###...", "...###...", "...###..."],
  "w": ["............", "............", "............", "............", "###..###..##", "###..###..##", ".##..###..##", ".##..###..##", ".##..#.#.###", ".#####.#.##.", "..####.####.", "..####.####.", "..###..####.", "..###..###..", "..###...##.."],
  "x": ["........", "........", "........", "........", "##...###", "###.###.", ".##.##..", ".#####..", "..###...", "..###...", "..###...", ".#####..", ".##.###.", "###.###.", "##...###"],
  "y": [".........", ".........", ".........", ".........", "###...###", ".##...##.", ".##...##.", ".###..##.", ".###.###.", "..##.##..", "..##.##..", "..##.##..", "...###...", "...###...", "...###...", "...###...", "...##....", ".####....", ".###....."],
  "z": ["........", "........", "........", "........", ".#######", ".#######", ".....###", "....###.", "...###..", "...###..", "..###...", ".###....", ".##.....", "########", "########"],
  ".": ["...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "###", "###", "###"],
  ",": ["...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "###", "###", "###", ".#.", ".#.", "##."],
  ":": ["...", "...", "...", "...", "###", "###", "###", "...", "...", "...", "...", "...", "###", "###", "###"],
  ";": ["...", "...", "...", "...", "###", "###", "###", "...", "...", "...", "...", "...", "###", "###", "###", "..#", "..#", "##."],
  "/": ["...##", "...##", "...#.", "..##.", "..##.", "..##.", "..#..", ".##..", ".##..", ".##..", ".#...", "##...", "##...", "##...", "#...."],
  "+": ["........", "........", "........", "........", "...##...", "...##...", "...##...", "...##...", "########", "########", "...##...", "...##...", "...##...", "...##...", "........"],
  "-": [".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", "#####", "#####", "#####", ".....", ".....", ".....", "....."],
  "=": ["........", "........", "........", "........", "........", "........", "########", "########", "........", "........", "########", "########", "........", "........", "........"],
  "%": ["..............", ".####.....#...", ".####....#....", ".#..#....#....", "##..##..#.....", "##..#...#.....", ".#..#..##.....", ".####..#......", "......##..###.", "......#..##.##", ".....##..##.##", ".....#...##.##", ".....#...##.##", "....#....##.##", "....#.....###."],
  "'": ["##", "##", "##", "##", "##", "..", "..", "..", "..", "..", "..", "..", "..", "..", ".."],
  "\"": ["#..#", "#..#", "#..#", "#..#", "#..#", "....", "....", "....", "....", "....", "....", "....", "....", "....", "...."],
  "!": ["###", "###", "###", "###", ".##", ".##", ".##", ".##", ".##", ".#.", ".#.", "...", ".##", ".##", ".##"],
  "(": ["..##", "..#.", ".##.", ".##.", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "###.", ".##.", ".##.", "..##", "..##"],
  ")": [".#...", ".##..", ".##..", "..##.", "..##.", "..##.", "..###", "..###", "...##", "...##", "..###", "..###", "..###", "..##.", "..##.", "..##.", ".##..", ".##..", "##..."],
  "*": [".##..", ".##..", "#####", "####.", ".###.", "##.#.", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", "....."],
  "<": ["........", "........", "........", "........", "........", "......##", "....####", "..####..", "####....", "###.....", "####....", ".#####..", "...#####", ".....###", ".......#"],
  ">": ["........", "........", "........", "........", "........", "##......", "####....", ".#####..", "...#####", ".....###", "....####", "..####..", "####....", "###.....", "#......."],
  "\u00b7": ["..", "..", "..", "..", "..", "..", "..", "..", "##", "##", "..", "..", "..", "..", ".."],
  "\u2014": ["................", "................", "................", "................", "................", "................", "................", "................", "################", "################", "................", "................", "................", "................", "................"],
  "?": ["..####..", ".######.", "###..###", "###...##", "##....##", ".....###", ".....###", "....###.", "...###..", "...##...", "...##...", "........", "...##...", "...##...", "...##..."],
};

/**
 * The roman family: cap 11 / x 8 / descent 4, two-pixel stems, CONDENSED.
 *
 * Why a serif at all. Every panel of the commercial game - the adventure
 * sidebar, the hero sheet, the town bar, the battle console, the kingdom
 * overview - is set in a roman serif; the one exception is the spellbook.
 * A blind reader put beside those references named our side correctly eight
 * times out of eight on 2026-09-08 and named the reason each time: ours was
 * the only panel set in a sans. That is a FACE problem and not a size one -
 * measured on `adventure/a reference screenshot.jpg`, the HD
 * Edition's sidebar hero name is a roman serif whose cap is 15 device pixels
 * at 1920x1080, which is exactly the cap ours already stood at.
 *
 * Why a new family rather than the serif faces the React side already ships
 * (`ui/src/theme/serifFace.ts`, C059 Bold): those are 30-36% wider at equal
 * cap (measured over twenty of this build's own strings: 1133 glyph columns
 * of the sans `tall` face against 1468 of `serif`, 1478 of `caption` against
 * 2002 of `serifCaption`), and the sidebar's gauge groove, the resource bar's
 * cells and the arena's plaques were all measured around the sans widths.
 * `Movement` sets 60 columns in `tall` and 81 in `serif`; it sets 62 here.
 *
 * GENERATED, like `tall` and `caption`, and kept in picture form so it is
 * edited the same way. Source: Noto Serif Condensed SemiBold (OFL,
 * `/usr/share/fonts/truetype/noto/NotoSerif-CondensedSemiBold.ttf`) rendered
 * by FreeType at 8x the target cap, condensed to 0.85 of its width, box-
 * filtered down to the cap height and thresholded at 120/255 - which is how a
 * hinted 1-bit render cannot be had at these caps, the hinter snapping 10 to
 * 12 and 14 to 16 with nothing in between. Clipped to the cap row, digits
 * padded to one shared width so a changing count does not jitter, space half
 * a digit. Regenerate with `genface.py` in this lane's scratch directory; the
 * picture IS the data, so a glyph may equally be redrawn by hand here.
 */
const ROMAN_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "..."],
  "0": [".####.", ".#..#.", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##", ".#..#.", ".####."],
  "1": ["..##..", "####..", "#.##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", ".####."],
  "2": [".###..", "##.##.", "#..##.", "...##.", "...#..", "..##..", "..#...", ".##...", ".#..#.", "#####.", "#####."],
  "3": [".###..", "##.##.", "#..##.", "...##.", "..##..", ".###..", "...##.", "...##.", "#..##.", "#..##.", "####.."],
  "4": ["...##.", "...##.", "..###.", "..###.", ".#.##.", ".#.##.", "#..##.", "######", "...##.", "...##.", "..####"],
  "5": [".####.", "#####.", "#.....", "#.....", "####..", "..###.", "...##.", "...##.", "...##.", "#..##.", "####.."],
  "6": [".####.", ".#.##.", "##....", "##....", "####..", "##.##.", "##.##.", "##.##.", "##.##.", "##.##.", ".###.."],
  "7": ["#####.", "#####.", "#..##.", "...#..", "...#..", "..##..", "..##..", "..#...", ".##...", ".##...", ".#...."],
  "8": [".####.", "##..##", "##..##", "##..##", ".####.", ".####.", "##.###", "##..##", "##..##", "##..##", ".####."],
  "9": [".###..", "##.##.", "##.##.", "##.##.", "##.##.", "#####.", ".####.", "...##.", "...##.", "#..#..", "####.."],
  "A": ["...##...", "...##...", "...##...", "..#.##..", "..#.##..", "..#.##..", "..####..", ".######.", ".#...##.", ".#...##.", "###..###"],
  "B": ["######.", ".##.###", ".##..##", ".##..##", ".##.##.", ".#####.", ".##..##", ".##..##", ".##..##", ".##..##", "######."],
  "C": ["..####", ".##.##", "##...#", "##....", "##....", "##....", "##....", "##....", "##....", ".##..#", "..####"],
  "D": ["######..", ".##..##.", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".##..##.", "######.."],
  "E": ["#######", ".##..##", ".##...#", ".##....", ".##....", ".#####.", ".##....", ".##....", ".##...#", ".##...#", "#######"],
  "F": ["######", ".##..#", ".##..#", ".##...", ".##...", ".####.", ".##...", ".##...", ".##...", ".##...", "####.."],
  "G": ["..#####.", ".##..##.", "##....#.", "##......", "##......", "##......", "##...###", "##...##.", "##...##.", ".##..##.", "..#####."],
  "H": ["####.####", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".#######.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", "####.####"],
  "I": ["####", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "####"],
  "J": [".###", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "###.", "##.."],
  "K": ["####.###", ".##..##.", ".##..#..", ".##.#...", ".####...", ".####...", ".####...", ".##.##..", ".##.##..", ".##..##.", "####..##"],
  "L": ["####...", ".##....", ".##....", ".##....", ".##....", ".##....", ".##....", ".##....", ".##...#", ".##...#", "#######"],
  "M": ["###....###", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".#.#..###.", ".#.###.##.", ".#.###.##.", ".#..##.##.", ".#..##.##.", "###.#..###"],
  "N": ["###..###", ".##...#.", ".###..#.", ".###..#.", ".#.##.#.", ".#.##.#.", ".#..###.", ".#..###.", ".#...##.", ".#...##.", "###..##."],
  "O": ["..####..", ".##..##.", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", ".##..##.", "..####.."],
  "P": ["######.", ".##..##", ".##..##", ".##..##", ".##..##", ".##.##.", ".####..", ".##....", ".##....", ".##....", "####..."],
  "Q": ["..####..", ".##..##.", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", ".##..##.", "..####..", "...##...", "...##...", "....##..", ".....##."],
  "R": ["######..", ".##..##.", ".##..##.", ".##..##.", ".##..##.", ".#####..", ".##.##..", ".##.##..", ".##..##.", ".##..##.", "####..##"],
  "S": [".####.", "##..##", "##..#.", "##....", ".###..", "..###.", "...###", "....##", "##..##", "##..##", ".####."],
  "T": ["#######", "#..#..#", "#..#..#", "...#...", "...#...", "...#...", "...#...", "...#...", "...#...", "...#...", "..###.."],
  "U": ["####.###", ".##...#.", ".##...#.", ".##...#.", ".##...#.", ".##...#.", ".##...#.", ".##...#.", ".##...#.", ".##..##.", "..####.."],
  "V": ["####.###", ".##...#.", ".##...#.", ".##..##.", "..#..#..", "..##.#..", "..##.#..", "..####..", "...##...", "...##...", "...##..."],
  "W": ["####..#..###", ".##..##...#.", ".##..###..#.", ".##..###..#.", ".##..###.##.", "..#..#.#.#..", "..###..#.#..", "..###..###..", "..###..###..", "...##..###..", "...##..##..."],
  "X": ["####.###", ".##..##.", "..##.#..", "..####..", "...##...", "...##...", "...###..", "..#.##..", "..#..##.", ".#...##.", "###.####"],
  "Y": ["####.###", ".##...#.", ".##...#.", "..##.#..", "..##.#..", "...##...", "...##...", "...##...", "...##...", "...##...", "..####.."],
  "Z": ["######", "#...##", "#..##.", "...##.", "..##..", "..##..", "..#...", ".##...", ".##..#", "##...#", "######"],
  "a": ["......", "......", "..##..", ".####.", ".#..#.", "...##.", ".####.", "##..#.", "##.##.", "##.##.", "###.##"],
  "b": [".##....", ".##....", ".##.#..", ".#####.", ".##..##", ".##..##", ".##..##", ".##..##", ".##..##", ".##..##", "######."],
  "c": [".....", ".....", "..##.", ".####", "##.##", "##...", "##...", "##...", "##...", "##..#", ".####"],
  "d": ["....##.", "....##.", "..#.##.", ".#####.", "##..##.", "##..##.", "##..##.", "##..##.", "##..##.", "##..##.", ".######"],
  "e": [".....", ".....", "..#..", ".###.", "##.##", "##.##", "#####", "##...", "##...", "##..#", ".####"],
  "f": [".##.#", ".##..", "####.", "####.", ".##..", ".##..", ".##..", ".##..", ".##..", ".##..", "####."],
  "g": ["......", "......", "..#.##", ".#####", "##..#.", "##..#.", ".#.##.", ".###..", ".#....", ".###..", ".#####", "##..##", "#...##", "##.##.", ".###.."],
  "h": [".##....", ".##....", ".##.#..", ".#####.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", "###..##"],
  "i": [".##.", "....", "....", "###.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "####"],
  "j": [".##", "...", "...", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", "##.", "#.."],
  "k": [".##....", ".##....", ".##....", ".##.##.", ".##.#..", ".####..", ".####..", ".####..", ".##.##.", ".##.##.", "####.##"],
  "l": [".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "####"],
  "m": ["...........", "...........", "....#..##..", "##########.", ".##..##.##.", ".##..#..##.", ".##..#..##.", ".##..#..##.", ".##..#..##.", ".##..##.##.", "###..##.###"],
  "n": [".......", ".......", "....#..", "######.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", "###..##"],
  "o": ["......", "......", "..##..", ".####.", "##..##", "##..##", "##..##", "##..##", "##..##", "##..##", ".####."],
  "p": [".......", ".......", "....#..", "######.", ".##..##", ".##..##", ".##..##", ".##..##", ".##..##", ".##..##", ".#####.", ".##....", ".##....", ".##....", "####..."],
  "q": [".......", ".......", "..#...#", ".######", "##..##.", "##..##.", "##..##.", "##..##.", "##..##.", "##..##.", ".#####.", "....##.", "....##.", "....##.", "...####"],
  "r": [".....", ".....", "...#.", "#####", ".##.#", ".#...", ".#...", ".#...", ".#...", ".#...", "###.."],
  "s": [".....", ".....", "..#..", "#####", "##.##", "##...", ".###.", "..###", "...##", "#..##", "####."],
  "t": ["....", ".##.", ".##.", "####", ".#..", ".#..", ".#..", ".#..", ".#..", ".##.", ".###"],
  "u": [".......", ".......", ".......", "###.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".##.##.", ".######"],
  "v": [".......", ".......", ".......", "###..##", ".##..#.", ".##..#.", ".##.##.", "..#.#..", "..###..", "..###..", "...#..."],
  "w": ["..........", "..........", "..........", "###..#..##", ".##.##..#.", ".##.##..#.", ".##.###.#.", "..##.###..", "..##..##..", "..##..##..", "..##..##.."],
  "x": [".......", ".......", ".......", "###.##.", ".##.##.", "..###..", "...##..", "..###..", "..#.#..", ".#..##.", "###.###"],
  "y": [".......", ".......", "#......", "###..##", ".##..#.", ".##..#.", "..#.##.", "..###..", "..###..", "..###..", "...#...", "...#...", "..##...", ".##....", "##....."],
  "z": [".....", ".....", ".##..", "#####", "#..#.", "..##.", "..#..", ".##..", ".##.#", "##..#", "#####"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "..", "..", "##", "##"],
  ",": ["..", "..", "..", "..", "..", "..", "..", "..", "..", "##", "##", ".#", "#."],
  ":": ["..", "..", "..", "##", "##", "..", "..", "..", "..", "##", "##"],
  ";": ["..", "..", "..", "##", "##", "..", "..", "..", "..", "##", "##", ".#", "#."],
  "/": ["..#", "..#", "..#", ".#.", ".#.", ".#.", ".#.", ".#.", ".#.", "#..", "#..", "#..", "#.."],
  "+": [".....", ".....", "..#..", "..#..", "..#..", "#####", "..#..", "..#..", "..#..", ".....", "....."],
  "-": ["...", "...", "...", "...", "...", "...", "###", "###", "...", "...", "..."],
  "=": [".....", ".....", ".....", "#####", "#####", ".....", "#####", "#####", ".....", ".....", "....."],
  "%": [".###..#...", "##.#..#...", "#..#..#...", "#..#.#....", "#..#.#.##.", "#######.##", ".##.#.#..#", "....#.#..#", "...#..#..#", "...#..#.##", "...#..###."],
  "'": ["##", "##", "##", "##", "..", "..", "..", "..", "..", "..", ".."],
  "\"": ["####", "####", "#..#", "#..#", "....", "....", "....", "....", "....", "....", "...."],
  "!": ["##", "##", "##", "##", "##", "##", "##", "..", "..", "##", "##"],
  "(": ["..##", ".##.", ".#..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", ".##.", "..#.", "...#"],
  ")": ["##.", ".#.", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".##", ".#.", "##.", "#.."],
  "*": ["#.#.#", "#####", ".###.", "#####", "..#..", ".....", ".....", ".....", ".....", ".....", "....."],
  "<": [".....", ".....", "....#", "..##.", ".##..", "##...", ".##..", "..##.", "....#", ".....", "....."],
  ">": [".....", ".....", "##...", ".##..", "..##.", "...##", "..##.", ".##..", "##...", ".....", "....."],
  "?": ["####.", "##.##", "#..##", "...##", "...##", "..##.", "..#..", "..#..", ".....", ".##..", ".##.."],
  "\u00b7": ["..", "..", "..", "..", ".#", "##", ".#", "..", "..", "..", ".."],
  "\u2014": [".............", ".............", ".............", ".............", ".............", ".............", "#############", "#############", ".............", ".............", "............."],
};

/**
 * The roman display face: cap 14 / x 11 / descent 5, two-pixel stems.
 *
 * The step a HUD reading takes when a call site asks for two pixels per texel
 * on a 768-tall window. The sans family had no 14 and reached that size by
 * DOUBLING its seven-row face, which is a small face drawn big and not a
 * bigger face; this is the one the resource bar's totals and the town's purse
 * are set in, so their figures keep the 14 device rows they had while the
 * family stays roman. On a 1080-tall window the floor promotes it to
 * `romanCaption` at 15, exactly as the doubled HUD face was promoted before.
 */
const ROMAN_DISPLAY_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "...."],
  "0": ["..###...", ".##.##..", "##...#..", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...#..", ".##.##..", "..###..."],
  "1": ["...##...", "..###...", "#####...", "#..##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", ".######."],
  "2": [".####...", "##..##..", "##...##.", "##...##.", "....##..", "....##..", "....##..", "...##...", "...#....", "..#.....", ".##...#.", ".#....#.", "#######.", "#######."],
  "3": [".#####..", "##..##..", "##..###.", ".#...##.", "....##..", "....##..", "..###...", "....##..", ".....##.", ".....##.", ".....##.", "#....##.", "##..##..", ".####..."],
  "4": ["....##..", "....##..", "...###..", "...###..", "..#.##..", "..#.##..", ".#..##..", ".#..##..", "#...##..", "########", "....###.", "....##..", "....##..", "...#####"],
  "5": [".######.", ".######.", ".#...##.", ".#......", ".#......", "##.#....", ".#####..", "....###.", ".....##.", ".....##.", ".....##.", "#....##.", "##..##..", ".####..."],
  "6": ["..####..", ".##..##.", ".##..##.", "##......", "##......", "##.##...", "######..", "##...##.", "##...##.", "##...##.", "##...##.", ".#...##.", ".##.##..", "..####.."],
  "7": ["#######.", "#######.", "#....#..", "#....#..", "....##..", "....##..", "....#...", "...##...", "...##...", "...#....", "..##....", "..##....", "..#.....", ".##....."],
  "8": ["..###...", ".##.##..", "##...##.", "##...##.", "###..#..", ".##.##..", "..###...", ".#####..", ".#..###.", "##...##.", "##...##.", "##...##.", "##...#..", ".#####.."],
  "9": [".####...", ".##.##..", "##...#..", "##...##.", "##...##.", "##...##.", "##...##.", ".######.", "..#..##.", ".....##.", ".....##.", "....##..", "##..##..", ".####..."],
  "A": ["....###....", "....###....", "....###....", "....###....", "...#..##...", "...#..##...", "...#..##...", "..##..##...", "..#######..", "..##..###..", "..#....##..", ".##....###.", ".##....###.", "####..#####"],
  "B": ["#######..", ".###.###.", ".###..###", ".###..###", ".###..##.", ".###..##.", ".######..", ".###..##.", ".###..###", ".###...##", ".###...##", ".###...##", ".###..##.", "#######.."],
  "C": ["..#####.", "..##..##", ".##...##", "###.....", "##......", "##......", "##......", "##......", "##......", "##......", "###.....", ".##....#", ".###..##", "..#####."],
  "D": ["#######...", ".###..##..", ".###...##.", ".###...##.", ".###...###", ".###....##", ".###....##", ".###....##", ".###....##", ".###...###", ".###...##.", ".###...##.", ".###..##..", "#######..."],
  "E": ["########", ".##...##", ".##...##", ".##.....", ".##.....", ".##.....", ".######.", ".###....", ".##.....", ".##.....", ".##.....", ".##....#", ".##...##", "########"],
  "F": ["########", ".###..##", ".##....#", ".##.....", ".##.....", ".##.....", ".######.", ".######.", ".##.....", ".##.....", ".##.....", ".##.....", ".###....", "#####..."],
  "G": ["...#####..", "..##...##.", ".##....##.", ".##.......", "##........", "##........", "##........", "##....####", "##....####", "###....##.", ".##....##.", ".##....##.", "..##...##.", "...#####.."],
  "H": ["#####.#####", ".###...###.", ".##....###.", ".##....###.", ".##....###.", ".##....###.", ".#########.", ".###...###.", ".##....###.", ".##....###.", ".##....###.", ".##....###.", ".###...###.", "#####.#####"],
  "I": ["#####", ".###.", ".##..", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".##..", ".###.", "#####"],
  "J": [".#####", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..###.", "..##..", "..##..", ".##...", "##...."],
  "K": ["####..###.", ".##....#..", ".##...##..", ".##...#...", ".##..#....", ".##..#....", ".##.##....", ".#####....", ".###.##...", ".##..##...", ".##...##..", ".##...##..", ".##...###.", "####...###"],
  "L": ["#####...", ".##.....", ".##.....", ".##.....", ".##.....", ".##.....", ".##.....", ".##.....", ".##.....", ".##.....", ".##....#", ".##....#", ".##...##", "########"],
  "M": ["####.....####", ".###.....###.", ".####...####.", ".####...####.", ".####...#.##.", ".#.##...#.##.", ".#..##.#..##.", ".#..##.#..##.", ".##.##.#..##.", ".##.####..##.", ".##..##...##.", ".##..##...##.", ".##..##..###.", "####.##.#####"],
  "N": ["####...####", ".###....##.", ".####...##.", ".####...##.", ".#####..##.", ".##.##..##.", ".##.###.##.", ".##..##.##.", ".##..##.##.", ".##...####.", ".##...####.", ".##....###.", ".##....###.", "####....##."],
  "O": ["...####...", "..##..##..", ".##....##.", "###....###", "###.....##", "##......##", "##......##", "##......##", "##......##", "###.....##", "###....###", ".##....##.", "..##..##..", "...####..."],
  "P": ["######..", ".##..##.", ".##...##", ".##...##", ".##...##", ".##...##", ".##..##.", ".######.", ".####...", ".##.....", ".##.....", ".##.....", ".##.....", "#####..."],
  "Q": ["...####...", "..##..##..", ".##....##.", "###....###", "###.....##", "##......##", "##......##", "##......##", "##......##", "##......##", "###....###", ".##....##.", ".###..###.", "..######..", "....##....", "....##....", ".....##...", ".....###..", ".......##."],
  "R": ["#######...", ".###..##..", ".###..###.", ".###..###.", ".###..###.", ".###..##..", ".###.###..", ".######...", ".###.##...", ".###.###..", ".###..##..", ".###..##..", ".###...##.", "#####..###"],
  "S": ["..####.", ".##.###", "##...##", "##.....", "##.....", ".###...", ".####..", "..####.", "....###", ".....##", "#....##", "##...##", "##...#.", ".####.."],
  "T": ["#########", "##.###.##", "#..###..#", "#..###..#", "...###...", "...###...", "...###...", "...###...", "...###...", "...###...", "...###...", "...###...", "...###...", "..#####.."],
  "U": ["#####.####", ".##....##.", ".##.....#.", ".##....##.", ".##....##.", ".##....##.", ".##....##.", ".##....##.", ".##....##.", ".##....##.", ".##....##.", "..##...#..", "..##..##..", "...####..."],
  "V": ["####..####", ".##.....#.", ".##....##.", "..##...#..", "..##...#..", "..##...#..", "..##..##..", "...##.#...", "...##.#...", "...##.#...", "...####...", "....##....", "....##....", "....##...."],
  "W": ["####...##..####", ".##....##...##.", ".##....##...##.", "..##...##...##.", "..##..####..#..", "..##..#.##..#..", "..##..#.##..#..", "..##..#.##..#..", "...##.#.##.##..", "...####..###...", "...###...###...", "...###...###...", "...###...###...", "....##....##..."],
  "X": ["#####.####", ".###...##.", "..##...#..", "..###.##..", "...##.#...", "...###....", "....##....", "....###...", "...####...", "...#.##...", "..#...##..", "..#...##..", ".##...###.", "####.#####"],
  "Y": ["#####.####", ".###...##.", "..##...##.", "..##...#..", "..###..#..", "...##.#...", "...##.#...", "....###...", "....##....", "....##....", "....##....", "....##....", "....##....", "...#####.."],
  "Z": ["########", "##...##.", "#....##.", "#...###.", "....##..", "...###..", "...##...", "...##...", "..##....", "..##....", ".##....#", ".##....#", "###...##", "########"],
  "a": [".......", ".......", ".......", "..##...", ".#####.", ".#..##.", "....##.", "....##.", ".#####.", "##..##.", "##..##.", "##..##.", "##..##.", ".##..##"],
  "b": [".##.....", ".##.....", ".##.....", ".##.##..", ".######.", ".###..##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".###.##.", "###.###."],
  "c": ["......", "......", "......", "..##..", ".#####", "##..##", "##..##", "##....", "##....", "##....", "##....", "##....", ".##..#", ".####."],
  "d": [".....##.", ".....##.", ".....##.", "..##.##.", ".######.", "##..###.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", ".##.###.", ".###.###"],
  "e": [".......", ".......", ".......", "...##..", ".#####.", ".##..##", "##...##", "##...##", "#######", "##.....", "##.....", "###....", ".##..##", "..####."],
  "f": ["..##.##", "..##.#.", "..##...", "#####..", "######.", "..##...", "..##...", "..##...", "..##...", "..##...", "..##...", "..##...", ".###...", "#####.."],
  "g": ["........", "........", "........", "...##.##", ".#####.#", ".##..#..", ".##..##.", ".##..##.", ".##..#..", "..####..", "..#.....", ".##.....", ".######.", ".######.", "##....##", "##....##", "##....#.", ".######.", "..###..."],
  "h": [".##......", ".##......", ".##......", ".##..#...", ".######..", ".###..##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", "####..###"],
  "i": [".###.", "..#..", ".....", "###..", "####.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", "#####"],
  "j": ["..##", "..#.", "....", ".###", ".###", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", ".###", "###.", "##.."],
  "k": [".##......", ".##......", ".##......", ".##..###.", ".##..###.", ".##..##..", ".##..#...", ".##.##...", ".#####...", ".#####...", ".##..##..", ".##..##..", ".###..##.", "####..###"],
  "l": [".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", ".##.", "####"],
  "m": [".............", ".............", ".............", "##..##...#...", ".######.###..", ".###.###..##.", ".##...##..##.", ".##...##..##.", ".##...##..##.", ".##...##..##.", ".##...##..##.", ".##...##..##.", ".##...##..##.", "####..###.###"],
  "n": [".........", ".........", ".........", "###..#...", ".######..", ".###..##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", "####..###"],
  "o": [".......", ".......", ".......", "..##...", ".#####.", "##...#.", "##...##", "##...##", "##...##", "##...##", "##...##", "##...##", ".##.##.", "..###.."],
  "p": ["........", "........", "........", "##..##..", "#######.", ".###..##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".##...##", ".###.##.", ".##.###.", ".##.....", ".##.....", ".##.....", "####....", "####...."],
  "q": ["........", "........", "........", "..##..##", ".#######", "##..###.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", "##...##.", ".##.###.", ".###.##.", ".....##.", ".....##.", ".....##.", "....####", "....####"],
  "r": [".......", ".......", ".......", "##...#.", "###.###", ".###.##", ".##....", ".##....", ".##....", ".##....", ".##....", ".##....", ".##....", "#####.."],
  "s": ["......", "......", "......", "..##..", ".#####", "##..##", "##....", "###...", ".####.", "..####", "....##", "#...##", "##..##", ".####."],
  "t": [".....", "..#..", "..#..", ".####", "#####", ".##..", ".##..", ".##..", ".##..", ".##..", ".##..", ".##..", ".###.", "..###"],
  "u": [".........", ".........", ".........", "###..##..", "###..###.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".##...##.", ".###.###.", "..###.###"],
  "v": [".........", ".........", ".........", "####..###", "####..###", ".##....#.", "..##..##.", "..##..#..", "..##..#..", "..##..#..", "...####..", "...###...", "...###...", "...###..."],
  "w": [".............", ".............", ".............", "####......###", "####..##..###", ".##...##...#.", ".##...##..##.", "..##.###..#..", "..##.#.##.#..", "..##.#.##.#..", "..####.####..", "...##...##...", "...##...##...", "...##...##..."],
  "x": [".........", ".........", ".........", ".###..##.", ".###..##.", "..##..#..", "..##.##..", "...###...", "...###...", "...###...", "..##.##..", "..#..##..", ".##..###.", "####.####"],
  "y": ["........", "........", "........", "###..###", "####.###", ".##...#.", ".##...#.", "..#..#..", "..##.#..", "..##.#..", "..####..", "...##...", "...##...", "...##...", "...##...", "...#....", "...#....", ".##.....", ".#......"],
  "z": [".......", ".......", ".......", ".#####.", "#######", "##..##.", "...###.", "...##..", "..###..", "..##...", ".###...", ".##...#", "###..##", "#######"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "##", "##"],
  ",": ["...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", ".#.", "###", ".##", ".##", ".#.", "#.."],
  ":": ["..", "..", "..", "##", "##", "..", "..", "..", "..", "..", "..", "..", "##", "##"],
  ";": ["...", "...", "...", "...", "###", "###", "...", "...", "...", "...", "...", ".#.", "###", ".##", ".##", ".#.", "#.."],
  "/": ["...#", "..##", "..#.", "..#.", "..#.", "..#.", "..#.", ".##.", ".#..", ".#..", ".#..", ".#..", ".#..", "##..", "#...", "#..."],
  "+": ["......", "......", "......", "..##..", "..##..", "..##..", "######", "######", "..##..", "..##..", "..##..", "......", "......", "......"],
  "-": ["....", "....", "....", "....", "....", "....", "....", "....", "####", "####", "....", "....", "....", "...."],
  "=": ["......", "......", "......", "......", "######", "######", "......", "......", "######", "######", "......", "......", "......", "......"],
  "%": [".###....#...", "##.##..##...", "##.##..#....", "##.##..#....", "##..#.#.....", "##.##.#..#..", "##.##.#.#.#.", ".###.#.##.##", "..#..#.#..##", "....##.#..##", "....#..#..##", "....#..##.##", "...##..##.##", "...#....###."],
  "'": ["##", "##", "##", "##", "##", "..", "..", "..", "..", "..", "..", "..", "..", ".."],
  "\"": ["##.##", "##.##", "##.##", "##.##", "##.##", ".....", ".....", ".....", ".....", ".....", ".....", ".....", ".....", "....."],
  "!": ["##", "##", "##", "##", "##", "##", "##", "##", "##", "..", "..", "..", "##", "##"],
  "(": ["..##", ".##.", ".##.", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", "##..", ".#..", ".##.", "..#.", "..##"],
  ")": ["##..", ".##.", ".##.", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..#.", ".##.", ".#..", "##.."],
  "*": ["..##..", "###.##", ".####.", ".####.", "###.##", "..##..", "..##..", "......", "......", "......", "......", "......", "......", "......"],
  "<": ["......", "......", ".....#", "....##", "...##.", ".##...", "##....", "##....", ".##...", "...##.", "....##", ".....#", "......", "......"],
  ">": ["......", "......", "#.....", "##....", ".##...", "...##.", "....##", "....##", "...##.", ".##...", "##....", "#.....", "......", "......"],
  "?": [".####.", "##..##", "##..##", "....##", "....##", "....##", "...##.", "..##..", "..#...", "..#...", "......", "..#...", "..##..", "..##.."],
  "\u00b7": ["..", "..", "..", "..", "..", "..", "##", "##", "..", "..", "..", "..", "..", ".."],
  "\u2014": [".................", ".................", ".................", ".................", ".................", ".................", ".................", ".................", "#################", ".................", ".................", ".................", ".................", "................."],
};

/**
 * The roman caption face: cap 15 / x 11 / descent 5, two-pixel stems.
 *
 * The 1080p floor's own face in the roman family - `deviceCapFloor` holds
 * every reading on a 1080-tall window to 15 device rows, and the HD Edition
 * stamps its own captions at 15-16 there. Same source and same generator as
 * the eleven-row face, so a reading promoted across the floor changes size
 * and nothing else.
 */
const ROMAN_CAPTION_PICTURES: Readonly<Record<string, Picture>> = {
  " ": ["....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "....", "...."],
  "0": ["..####..", ".##..##.", ".##..##.", "###..###", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "##....##", "###..###", ".##..##.", ".##..##.", "..####.."],
  "1": ["...##...", "..###...", "#####...", "##.##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", "...##...", ".######."],
  "2": [".####...", "##..##..", "##..###.", "##...##.", "....###.", "....##..", "....##..", "....#...", "...##...", "..##....", "..#.....", ".##...#.", "##....#.", "#######.", "#######."],
  "3": [".####...", "###.##..", "##..###.", "##...##.", "....##..", "....##..", "..###...", "..####..", "....##..", ".....##.", ".....##.", "#....##.", "##...##.", "##..##..", ".####..."],
  "4": ["....##..", "....##..", "...###..", "...###..", "..#.##..", "..#.##..", ".##.##..", ".#..##..", ".#..##..", "##..###.", "########", "....##..", "....##..", "....###.", "...#####"],
  "5": [".######.", ".######.", ".#...##.", ".#......", ".#......", "##......", "#####...", "....##..", ".....##.", ".....##.", ".....##.", ".....##.", "#....##.", "##..##..", ".####..."],
  "6": ["..####..", ".##..##.", ".##..##.", "##......", "##......", "##......", "######..", "###.###.", "##...##.", "##...##.", "##...##.", "##...##.", ".#...##.", ".##.##..", "..###..."],
  "7": ["#######.", "#######.", "#....##.", "#....#..", "....##..", "....##..", "....#...", "...##...", "...##...", "...##...", "...#....", "..##....", "..##....", "..#.....", ".##....."],
  "8": ["..####..", ".##..##.", "###..##.", "##....##", "###..##.", ".##..##.", ".#####..", "..####..", ".##.###.", "##...###", "##....##", "##....##", "##....##", ".##..##.", ".#####.."],
  "9": ["..###...", ".##.##..", "##...#..", "##...##.", "##...##.", "##...##.", "##...##.", ".##.###.", ".###.##.", ".....##.", ".....##.", ".....#..", "....##..", "##..##..", ".####..."],
  "A": [".....##....", "....###....", "....###....", "....###....", "...##.##...", "...#..##...", "...#..##...", "...#..##...", "..##..###..", "..#######..", "..#....##..", "..#....##..", ".##....###.", ".##....###.", "####..#####"],
  "B": ["#######..", ".###.###.", ".###..##.", ".###..###", ".###..###", ".###..##.", ".######..", ".######..", ".###..##.", ".###...##", ".###...##", ".###...##", ".###...##", ".###..##.", "#######.."],
  "C": ["...#####.", "..##..###", ".##....##", ".##....#.", "###......", "###......", "###......", "##.......", "###......", "###......", "###......", ".##......", ".##.....#", "..###..##", "...#####."],
  "D": ["#######...", ".###..##..", ".###...##.", ".###...##.", ".###...###", ".###....##", ".###....##", ".###....##", ".###....##", ".###....##", ".###...###", ".###...##.", ".###...##.", ".###..##..", "#######..."],
  "E": ["########.", ".###...#.", "..##...##", ".###...#.", ".###.....", ".###.....", ".######..", ".######..", ".###.....", ".###.....", ".###.....", ".###....#", "..##....#", ".###...##", "#########"],
  "F": ["########", ".###..##", ".##....#", ".###...#", ".##.....", ".##.....", ".###....", ".######.", ".###....", ".##.....", ".###....", ".###....", ".##.....", ".###....", "#####..."],
  "G": ["...#####..", "..##..###.", ".##....##.", ".##....#..", "###.......", "##........", "##........", "##........", "##....####", "##.....##.", "###....##.", ".##....##.", ".##....##.", "..##...##.", "...#####.."],
  "H": ["#####..#####", ".###....###.", "..##....##..", "..##....##..", "..##....##..", "..##....##..", "..########..", "..########..", "..##....##..", "..##....##..", "..##....##..", "..##....##..", "..##....##..", ".###....###.", "#####..#####"],
  "I": ["#####", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", "#####"],
  "J": [".#####", "..###.", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", "..##..", ".###..", "###..."],
  "K": ["#####.####.", ".###...##..", "..##...##..", ".###..##...", ".###..#....", ".###.##....", ".###.#.....", ".######....", ".######....", ".###.###...", ".###..##...", ".###..###..", "..##...##..", ".###...###.", "#####...###"],
  "L": ["#####....", ".###.....", "..##.....", ".###.....", ".###.....", ".###.....", ".###.....", ".###.....", ".###.....", ".###.....", ".###....#", ".###....#", "..##....#", ".###...##", "#########"],
  "M": ["####.....#####", ".####....####.", "..###....###..", ".####....###..", ".####...####..", ".##.##..#.##..", ".##.##..#.##..", ".##.##..#.##..", ".##.##..#.##..", ".##.#####.##..", ".##..###..##..", ".##..###..##..", ".##..###..##..", ".##...#...###.", "####..#..#####"],
  "N": ["####...####", ".###....##.", ".####...##.", ".####...##.", ".####...##.", ".##.##..##.", ".##.##..##.", ".##..##.##.", ".##..##.##.", ".##...####.", ".##...####.", ".##...####.", ".##....###.", ".##....###.", "####....##."],
  "O": ["...####...", "..##..##..", ".##....##.", "###....##.", "###....###", "##......##", "##......##", "##......##", "##......##", "##......##", "###....###", ".##....##.", ".##....##.", "..##..##..", "...####..."],
  "P": ["#######..", ".###..##.", "..##..###", ".###...##", "..##...##", "..##...##", "..##..###", "..##..##.", ".######..", ".###.....", "..##.....", ".###.....", "..##.....", ".###.....", "#####...."],
  "Q": ["...####...", "..##..##..", ".##....##.", ".##....##.", "###....###", "##......##", "##......##", "##......##", "##......##", "##......##", "##.....###", "###....##.", ".##....##.", ".###..##..", "..######..", "....##....", "....##....", "....###...", ".....###..", "......###."],
  "R": ["#######...", ".###.###..", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".###..##..", ".######...", ".######...", ".###.##...", ".###.###..", ".###..##..", ".###..###.", ".###...##.", "#####..###"],
  "S": ["..#####.", ".##..##.", "##....##", "##....#.", "###.....", ".##.....", ".####...", "..#####.", "....###.", ".....###", "......##", "##....##", "##....##", "###..##.", ".#####.."],
  "T": ["##########", "##..##..##", "#...##...#", "#...##...#", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "....##....", "..######.."],
  "U": ["#####..####", ".###....##.", "..##....##.", ".###....##.", ".###....##.", ".###....##.", ".###....##.", ".###....##.", ".###....##.", ".###....##.", ".###....##.", "..##....##.", "..##....#..", "..###..##..", "...#####..."],
  "V": ["#####..####", ".###....##.", ".###....##.", "..##....#..", "..##....#..", "..##...##..", "..###..##..", "...##..#...", "...##..#...", "...##..#...", "...#####...", "....###....", "....###....", "....###....", "....##....."],
  "W": ["#####...#...####", ".###....##...##.", ".###...###...##.", "..##...###...#..", "..##...###...#..", "..##...###...#..", "..##..##.##..#..", "..###.#..##.##..", "...##.#..##.##..", "...##.#..##.#...", "...####..##.#...", "...####...###...", "...###....###...", "....##....##....", "....##....##...."],
  "X": ["#####..####", ".###....##.", "..###...#..", "..###..#...", "...##..#...", "...####....", "....###....", "....###....", "....###....", "...#####...", "...#..##...", "..##..###..", "..#....##..", ".##....###.", "####..#####"],
  "Y": ["#####..####", ".###....##.", "..##....##.", "..##....#..", "..###..##..", "...##..#...", "...###.#...", "....###....", "....###....", "....###....", "....###....", "....###....", "....###....", "....###....", "...#####..."],
  "Z": ["#########", "##....##.", "##...###.", "##...##..", "....###..", "....###..", "....##...", "...###...", "...##....", "..###....", "..##.....", ".###....#", ".##.....#", "###....##", "#########"],
  "a": ["........", "........", "........", "........", ".#####..", ".##..##.", ".##..##.", ".....##.", "..#####.", ".######.", "##...##.", "##...##.", "##...##.", "###.###.", ".###.###"],
  "b": [".###.....", ".###.....", ".###.....", ".##......", ".#######.", ".###..##.", ".###...##", ".###...##", ".###...##", ".###...##", ".###...##", ".###...##", ".###..###", ".####.##.", "###.####."],
  "c": [".......", ".......", ".......", ".......", ".#####.", ".##..##", "###..##", "##.....", "##.....", "##.....", "##.....", "##.....", "###...#", ".###.##", "..####."],
  "d": [".....###.", "......##.", ".....###.", "......##.", ".#######.", ".##..###.", "###..###.", "##...###.", "##...###.", "##....##.", "##...###.", "##...###.", "###..###.", ".##.####.", ".####.###"],
  "e": [".......", ".......", ".......", ".......", ".#####.", ".##.##.", "##...##", "##...##", "#######", "#####..", "##.....", "##.....", "###....", ".##..##", "..####."],
  "f": ["..##.##", "..##.##", "..##...", ".###...", "######.", ".###...", ".###...", ".###...", ".###...", ".###...", ".###...", ".###...", ".###...", ".###...", "#####.."],
  "g": ["........", "........", "........", "......##", "..######", ".##..#..", ".##..##.", ".#...##.", ".##..##.", ".##.##..", "..###...", ".##.....", ".##.....", ".######.", ".######.", "##....##", "##....##", "##....#.", "##...##.", ".#####.."],
  "h": [".###......", ".###......", ".###......", ".###......", ".#######..", ".###..###.", ".###..###.", ".###...##.", ".###...##.", ".###...##.", ".###...##.", ".###...##.", ".###...##.", ".###...##.", "#####..###"],
  "i": [".###.", "..#..", ".....", ".....", "####.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", "#####"],
  "j": ["..##", "..##", "....", "....", ".###", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", "..##", ".##.", "##.."],
  "k": [".##......", ".##......", ".##......", ".##......", ".##..###.", ".##...#..", ".##..##..", ".##..#...", ".##.##...", ".#####...", ".######..", ".##..##..", ".##..###.", ".###..##.", "#####.###"],
  "l": [".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", ".###.", "#####"],
  "m": ["..............", "..............", "..............", "..............", "###.###..###..", ".###.####.###.", ".###..##...##.", ".##...##...##.", ".##...##...##.", ".##...##...##.", ".##...##...##.", ".##...##...##.", ".##...##...##.", ".###..##...##.", "####..###..###"],
  "n": ["..........", "..........", "..........", "..........", "###.####..", ".###..##..", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".###..###.", ".###..###.", "#####.####"],
  "o": ["........", "........", "........", "........", "..####..", ".##..##.", "##...###", "##....##", "##....##", "##....##", "##....##", "##....##", "###..###", ".##..##.", "..####.."],
  "p": [".........", ".........", ".........", ".........", "###.####.", ".###..##.", ".###..###", ".###...##", ".##....##", ".##....##", ".###...##", ".###...##", ".###..###", ".####.##.", ".##.###..", ".##......", ".##......", ".##......", ".###.....", "#####...."],
  "q": [".........", ".........", ".........", ".........", ".####.###", ".##..###.", "###..###.", "##...###.", "##...###.", "##....##.", "##...###.", "##...###.", "###..###.", ".##.####.", "..###.##.", "......##.", ".....###.", "......##.", ".....###.", "....#####"],
  "r": [".......", ".......", ".......", ".......", "###.###", ".###.##", ".###.##", ".##....", ".##....", ".##....", ".##....", ".##....", ".##....", ".###...", "#####.."],
  "s": ["......", "......", "......", "......", ".####.", "##..##", "##..#.", "##....", ".###..", ".####.", "...###", "....##", "#...##", "##..##", ".####."],
  "t": ["......", "..##..", "..##..", ".###..", "######", ".###..", ".###..", ".###..", ".###..", ".###..", ".###..", ".###..", ".###..", "..##..", "..####"],
  "u": ["..........", "..........", "..........", "..........", "####.####.", ".##...###.", ".##...###.", ".##...###.", ".##...###.", ".##...###.", ".##...###.", ".##...###.", ".###..###.", "..##.####.", "..####.###"],
  "v": [".........", ".........", ".........", ".........", "####..###", ".##....#.", ".##....#.", "..##..#..", "..##..#..", "..##..#..", "..##..#..", "...###...", "...###...", "...###...", "...###..."],
  "w": [".............", ".............", ".............", ".............", "####..##..###", ".##...##...#.", ".##...##..##.", "..##..##..#..", "..##.####.#..", "..##.#.##.#..", "..##.#.##.#..", "..####.####..", "...##...##...", "...##...##...", "...##...##..."],
  "x": ["..........", "..........", "..........", "..........", "#####.###.", "..##...#..", "..###.##..", "...####...", "...###....", "....##....", "...####...", "...#.###..", "..#...##..", ".##...###.", "####.#####"],
  "y": [".........", ".........", ".........", ".........", "####..###", ".##....#.", ".##...##.", "..##..#..", "..##..#..", "..##..#..", "...#..#..", "...###...", "...###...", "...###...", "....#....", "....#....", "...##....", "...##....", "..##.....", "###......"],
  "z": [".......", ".......", ".......", ".......", "#######", "##..##.", "#...##.", "...##..", "...##..", "..##...", "..##...", ".###..#", ".##...#", "###..##", "#######"],
  ".": ["..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "..", "##", "##", "##"],
  ",": ["...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", "...", ".#.", "###", ".##", ".##", ".#.", "#.."],
  ":": ["..", "..", "..", "..", "##", "##", "##", "..", "..", "..", "..", "..", "##", "##", "##"],
  ";": ["...", "...", "...", "...", "###", "###", ".#.", "...", "...", "...", "...", "...", ".#.", "###", ".##", ".##", ".#.", "##."],
  "/": ["...#", "...#", "..##", "..#.", "..#.", "..#.", "..#.", "..#.", ".##.", ".#..", ".#..", ".#..", ".#..", ".#..", "##..", "#...", "#...", "#..."],
  "+": [".......", ".......", ".......", "...#...", "...#...", "...#...", "...#...", "#######", "...#...", "...#...", "...#...", "...#...", ".......", ".......", "......."],
  "-": ["....", "....", "....", "....", "....", "....", "....", "....", "####", "####", "....", "....", "....", "....", "...."],
  "=": [".......", ".......", ".......", ".......", ".......", "#######", ".......", ".......", ".......", "#######", ".......", ".......", ".......", ".......", "......."],
  "%": [".###....##...", "##..#...#....", "##..#...#....", "##..##.##....", "##..##.#.....", "##..#..#.....", "##..#.#..###.", ".#.##.#.##.##", ".###.##.#..##", ".....#..#..##", ".....#.##..##", "....##.##..##", "....#...#..##", "....#...##.##", "...##....###."],
  "'": ["##", "##", "##", "##", "##", "..", "..", "..", "..", "..", "..", "..", "..", "..", ".."],
  "\"": ["##..##", "##..##", "##..##", "##..##", ".#..#.", "......", "......", "......", "......", "......", "......", "......", "......", "......", "......"],
  "!": ["###", "###", "###", "###", "###", "###", ".#.", ".#.", ".#.", ".#.", "...", "...", "##.", "###", "###"],
  "(": ["...#.", "..##.", ".##..", ".##..", "###..", "###..", "###..", "##...", "##...", "###..", "###..", "###..", "###..", ".##..", ".##..", "..##.", "...##", "....#"],
  ")": ["##...", ".##..", "..##.", "..##.", "..###", "..###", "..###", "...##", "...##", "...##", "..###", "..###", "..###", "..##.", "..##.", ".##..", "##...", "#...."],
  "*": ["..##..", "##.###", ".####.", ".####.", "######", "..##..", "..##..", "......", "......", "......", "......", "......", "......", "......", "......"],
  "<": [".......", ".......", ".......", ".....##", "....##.", "..###..", ".##....", "##.....", ".##....", "..###..", "....##.", ".....##", ".......", ".......", "......."],
  ">": [".......", ".......", ".......", "##.....", ".##....", "..###..", "....##.", ".....##", "....##.", "..###..", ".##....", "##.....", ".......", ".......", "......."],
  "?": [".#####.", "###.##.", "##...##", ".#...##", ".....##", "....###", "....##.", "...##..", "..##...", "...#...", ".......", ".......", "..##...", "..##...", "..##..."],
  "\u00b7": ["..", "..", "..", "..", "..", "..", "##", "##", "##", "..", "..", "..", "..", "..", ".."],
  "\u2014": ["..................", "..................", "..................", "..................", "..................", "..................", "..................", "..................", "..................", "##################", "..................", "..................", "..................", "..................", ".................."],
};

/**
 * The eight faces, by id. Every face carries the same key set - a character
 * that draws in one and folds to `?` in another would be a caption that changed
 * its wording when a screen changed its size, so `bitmap-text.spec.ts` holds
 * the five tables to one another.
 */
export const FACES: Readonly<Record<FaceId, Face>> = Object.freeze({
  hud: Object.freeze({
    id: "hud" as const,
    capH: GLYPH_H, xHeight: 5, descent: DESCENT, stem: 1, tracking: TRACKING,
    glyphs: GLYPHS,
  }),
  text: pictureFace("text", 9, 6, 2, 1, 1, TEXT_PICTURES),
  tall: pictureFace("tall", 11, 8, 3, 2, 1, TALL_PICTURES),
  display: pictureFace("display", 11, 7, 3, 2, 1, DISPLAY_PICTURES),
  caption: pictureFace("caption", 15, 11, 4, 2, 1, CAPTION_PICTURES),
  roman: pictureFace("roman", 11, 8, 4, 2, 1, ROMAN_PICTURES),
  romanDisplay: pictureFace("romanDisplay", 14, 11, 5, 2, 1, ROMAN_DISPLAY_PICTURES),
  romanCaption: pictureFace("romanCaption", 15, 11, 5, 2, 1, ROMAN_CAPTION_PICTURES),
});

/** The face `id` names; the HUD face when nothing is named. */
export function faceOf(id?: FaceId): Face {
  return FACES[id ?? DEFAULT_FACE];
}

/**
 * The entry a face draws `ch` with. A character with no design folds to `?`,
 * which is visible in a screenshot rather than silently swallowing a reading.
 *
 * Lowercase used to fold up to its capital. It does not any more - the face has
 * a lowercase now, and folding it away is what made every plaque caption small
 * caps whether or not the screen asked for that.
 */
export function foldGlyphKey(ch: string, face?: FaceId): string {
  const table = faceOf(face).glyphs;
  if (Object.prototype.hasOwnProperty.call(table, ch)) return ch;
  // A capital is a better answer than `?` for a letter whose lowercase or
  // accented form this face has no design for.
  const up = ch.toUpperCase();
  return Object.prototype.hasOwnProperty.call(table, up) ? up : "?";
}

/** The design `ch` draws with in `face`, never undefined. */
export function glyphFor(ch: string, face?: FaceId): Glyph {
  const f = faceOf(face);
  return f.glyphs[foldGlyphKey(ch, face)]!;
}
